<script>
  import { afterUpdate, onDestroy, tick } from 'svelte';
  import { combatStageFocus, isTextEntry, prefersReducedMotion, rarityClass } from '../keyboardShortcuts.js';
  import { phaseCaption, phaseBanner } from '../bossPhases.js';
  import { battleDockOpen } from '../uiChrome.js';
  import { battleSpriteSrc, enemySilhouette, playerSilhouette } from '../portraitSrc.js';
  import {
    skillDisplayName,
    isConsumableItem,
    normalizeHotbarBinds,
    resolveHotbarActivation,
    findInventoryItem,
    skillGenericArtUrl,
    actionGenericArtUrl,
    characterClassId,
    armsScrap,
  } from '../hudPrefs.js';
  import { classCatalog } from '../../onboarding/classCatalog.js';
  import { settingsStore } from '../SettingsStore.js';
  import { resolveBattleLayoutB } from '../battleLayout.js';
  import { overlayStore } from './overlayStore.js';
  import { itemArtSrc, onItemArtError } from '../itemArtSrc.js';
  import { backend } from '../../api/base.js';
  import { livingFocus } from '../combatFocus.js';
  import { combatEventIcon } from '../combatEvent.js';
  import { sortLootUniqueFirst, uniqueDropName } from '../itemEffects.js';
  import {
    DEFAULT_DECISION_WINDOW_MS,
    DEFAULT_BEAT_BUDGET_MS,
    createSkillCooldownClock,
  } from '../combatCooldown.js';

  export let store;
  export let sendMessage;
  export let shown = true;

  const cdClock = createSkillCooldownClock();

  let panel = null; // null | 'items'
  let nowMs = Date.now();
  let tickTimer = null;
  let fxKey = 0;
  let bannerText = '';
  let bannerParts = null; // { kind, actor, verb, target, amount, icon, raw }
  let bannerVisible = false;
  let bannerTimer = null;
  let lastBannerKey = '';
  let bannerFlashKey = 0;
  let arenaFlash = false;
  let arenaFlashTimer = null;
  let logEl = null;
  let logScrollPending = false;
  let logExpanded = false;

  $: phase = $store.combatPhase || ($store.inCombat ? 'active' : 'idle');
  $: visible = shown && (phase === 'active' || phase === 'ending');
  $: if (!visible) logExpanded = false;
  $: enemies = $store.combatEnemies || [];
  $: phaseNotice = phaseBanner($store.combatPhaseEnter, nowMs, visible);
  $: windupEnemy = enemies.find((e) => e && e.telegraph && (e.hp ?? 0) > 0);
  $: windupText = windupEnemy
    ? `${windupEnemy.name} is winding up ${windupEnemy.telegraph}!`
    : "";
  $: players = $store.combatPlayers || [];
  $: allies = players.filter((p) => p.id !== selfCombatant?.id);
  $: combatJoin = $store.combatJoin;
  $: showJoinBanner = !!(combatJoin?.at && nowMs - combatJoin.at < 3000);
  $: targetId = $store.combatTargetId;
  $: focusedEnemy = (enemies || []).find((e) => e && e.id === targetId) || null;
  $: threatWarning = $store.combatThreatWarning;
  $: turn = $store.combatTurn;
  $: logRaw = $store.combatLog || [];
  $: log = (logRaw || []).filter((line) => line && (line.kind === "combatEvent" || !isCombatLogNoise(line.text)));
  $: outcome = $store.combatOutcome;
  $: endMessage = $store.combatEndMessage || '';
  $: rewardBreakdown = $store.combatRewards;

  function signedReward(n) {
    const v = Number(n) || 0;
    return v > 0 ? `+${v}` : String(v);
  }
  $: fx = $store.combatFx;
  $: character = $store.character;
  $: stats = $store.characterStats || {};
  $: roomBackground = $store.background || '';
  $: arenaBgUrl = roomBackground
    ? `${backend}/backgrounds/${roomBackground}.png`
    : '';
  $: inventory = $store.inventory || [];
  $: equippedSkills = stats.equippedSkills || character?.equippedSkills || [];
  $: selfId = character?.id || '';
  $: selfCombatant = players.find((p) => p.id === selfId) || players[0] || null;

  $: selfHp = selfCombatant?.hp ?? stats.currentHitPoints ?? 0;
  $: selfMaxHp = selfCombatant?.maxHp ?? stats.maxHitPoints ?? 1;
  $: selfMana = stats.currentMana ?? 0;
  $: selfMaxMana = stats.maxMana ?? 0;
  $: selfName = selfCombatant?.name || character?.name || 'You';
  $: selfClass = typeof character?.class === 'object'
    ? (character.class.name || '')
    : (character?.class || '');

  $: isMyTurn = !!(turn && selfId && turn.actorId === selfId);
  $: nextActionAtMs = Number($store.combatNextActionAtMs) || 0;
  $: waitingOnBeat = phase === 'active' && nextActionAtMs > nowMs;
  $: decisionActive = isMyTurn && !waitingOnBeat && Number(turn?.deadlineMs) > nowMs;
  $: deadlineMs = decisionActive ? (turn?.deadlineMs || 0) : 0;
  $: timerLeftMs = deadlineMs > 0 ? Math.max(0, deadlineMs - nowMs) : 0;
  let decisionWindowMs = DEFAULT_DECISION_WINDOW_MS;
  let lastDeadlineMs = 0;
  $: if (deadlineMs > 0 && deadlineMs !== lastDeadlineMs) {
    lastDeadlineMs = deadlineMs;
    const remaining = deadlineMs - Date.now();
    decisionWindowMs = remaining > 250 ? remaining : DEFAULT_DECISION_WINDOW_MS;
  }
  $: timerPct = deadlineMs > 0 && decisionWindowMs > 0
    ? Math.max(0, Math.min(100, (timerLeftMs / decisionWindowMs) * 100))
    : 0;
  $: timerSec = Math.ceil(timerLeftMs / 1000);
  $: showWaitingTimer = phase === 'active' && !decisionActive;
  $: beatLeftMs = waitingOnBeat ? Math.max(0, nextActionAtMs - nowMs) : 0;
  $: beatLeftSec = Math.ceil(beatLeftMs / 1000);
  $: waitingLabel = waitingOnBeat
    ? (beatLeftSec > 0 ? `Next ${beatLeftSec}s` : 'Resolving…')
    : (isMyTurn ? 'Resolving…' : ((turn?.actorName ? `${turn.actorName}` : 'Waiting') + '…'));
  $: enemyCount = (enemies || []).length;
  $: enemyPack = enemyCount <= 0 ? '' : (enemyCount === 1 ? 'solo' : (enemyCount <= 3 ? 'duo' : 'swarm'));

  $: if (fx?.at) fxKey = fx.at;

  $: fxActive = !!(fx && fxKey);
  $: fxId = (fx && fx.fxId) || '';
  $: fxResult = (fx && fx.result) || '';
  $: fxDamage = Number(fx && fx.damage) || 0;
  $: fxHeal = Number(fx && fx.heal) || 0;
  $: fxIsMiss = fxActive && (fxId === 'miss' || fxResult === 'miss' || fxResult === 'dodged');
  $: fxIsDeath = fxActive && fxId === 'death';
  $: fxIsCast = fxActive && (fxId === 'cast' || fxResult === 'cast');
  $: fxIsDefend = fxActive && (fxId === 'defend' || fxResult === 'defended' || fxResult === 'block');
  $: fxIsFlee = fxActive && (fxId === 'flee' || fxResult === 'fled');
  $: fxIsHit = fxActive && !fxIsMiss && !fxIsDefend && !fxIsFlee && (
    fxId === 'slash' || fxIsDeath || fxResult === 'hit' || fxResult === 'crit' || fxDamage > 0
  );
  $: fxIsCrit = fxActive && fxResult === 'crit';
  $: fxAbility = (fx && fx.ability) || '';
  $: fxIsCrush = fxActive && /crushing blow/i.test(fxAbility);

  $: lootList = sortLootUniqueFirst(Array.isArray(rewardBreakdown?.loot) ? rewardBreakdown.loot : []);
  $: levelUp = rewardBreakdown?.levelUp || null;
  $: defeatInfo = rewardBreakdown?.defeat || null;
  $: hasRewardBreakdown = !!(rewardBreakdown && (
    rewardBreakdown.baseXp != null || rewardBreakdown.xp != null || rewardBreakdown.baseGold != null
  ));

  let lootShown = 0;
  let lootTimer = null;
  let armedLootKey = '';

  function stopLootReveal() {
    if (lootTimer) {
      clearInterval(lootTimer);
      lootTimer = null;
    }
  }

  function armLootReveal(items) {
    stopLootReveal();
    const list = Array.isArray(items) ? items : [];
    if (prefersReducedMotion() || list.length === 0) {
      lootShown = list.length;
      return;
    }
    lootShown = 0;
    lootTimer = setInterval(() => {
      lootShown += 1;
      if (lootShown >= list.length) stopLootReveal();
    }, 420);
  }

  $: if (phase === 'ending') {
    const names = lootList.map((item) => item && item.name).join('|');
    const key = `${outcome}|${names}|${levelUp?.newLevel || 0}`;
    if (key !== armedLootKey) {
      armedLootKey = key;
      armLootReveal(lootList);
    }
  } else if (armedLootKey) {
    armedLootKey = '';
    stopLootReveal();
    lootShown = 0;
  }

  $: levelReady = !levelUp || lootShown >= lootList.length;

  function onOutcomeKey(event) {
    if (phase !== 'ending') return;
    if (isTextEntry(event.target)) return;
    if (event.key !== 'Enter' && event.key !== 'Escape') return;
    event.preventDefault();
    dismissOutcome();
  }

  $: if (visible && (phase === 'active' || phase === 'ending')) startTick();
  else stopTick();

  $: consumables = inventory.filter(isConsumableItem);
  $: skillEntries = (equippedSkills || []).map((s) => {
    if (typeof s === 'string') return { id: s, name: skillDisplayName(s) };
    const id = s.id || s.skillId || '';
    const name = s.name || skillDisplayName(id);
    return { id, name };
  }).filter((s) => s.name);

  $: hotbarBinds = normalizeHotbarBinds($settingsStore.interface?.hotbarBinds);

  // Layout B default: URL > localStorage 0/1 > Settings. No preference → B.
  $: layoutB = resolveBattleLayoutB({
    settingsFlag: $settingsStore.interface?.battleLayoutB,
  });

  $: queuedAction = $store.combatQueuedAction || '';
  $: queuedSkillId = $store.combatQueuedSkillId || '';
  $: skillCooldowns = $store.combatSkillCooldowns || {};
  $: decisionDeadlineMs = Number($store.combatDecisionDeadlineMs) || deadlineMs || 0;
  $: if (isMyTurn && turn) {
    cdClock.noteSelfTurn(`${turn.round}-${turn.actorId}-${turn.deadlineMs || 0}`, nowMs);
  }
  $: {
    skillCooldowns;
    cdClock.sync(skillCooldowns, nowMs);
  }
  $: if (!visible) cdClock.reset();
  $: resolveAtMs = nextActionAtMs > 0
    ? nextActionAtMs
    : (queuedAction && decisionDeadlineMs > 0 ? decisionDeadlineMs : 0);
  $: queueLeftMs = resolveAtMs > 0 ? Math.max(0, resolveAtMs - nowMs) : 0;
  $: queueLeftSec = Math.ceil(queueLeftMs / 1000);
  $: queuedLabel = queuedChipLabel(queuedAction, queuedSkillId);


  // One concise banner per structured action. Prose stays in the log.
  $: {
    let nextParts = null;
    let nextKey = '';
    if (fx && fx.at && fx.action !== 'phase-enter') {
      nextKey = `fx-${fx.at}`;
      nextParts = formatFxBannerParts(fx);
    }
    if (nextParts && nextKey && nextKey !== lastBannerKey) {
      lastBannerKey = nextKey;
      bannerParts = nextParts;
      bannerText = nextParts.raw || '';
      bannerVisible = true;
      bannerFlashKey += 1;
      if (bannerTimer) clearTimeout(bannerTimer);
      bannerTimer = setTimeout(() => {
        bannerVisible = false;
        bannerTimer = null;
      }, 2800);
      const kind = nextParts.kind;
      if (kind === 'hit' || kind === 'crit' || kind === 'heal') {
        arenaFlash = true;
        if (arenaFlashTimer) clearTimeout(arenaFlashTimer);
        arenaFlashTimer = setTimeout(() => {
          arenaFlash = false;
          arenaFlashTimer = null;
        }, kind === 'crit' ? 520 : 380);
      }
    }
  }

  // Keep combat log pinned to latest lines (after DOM paint).
  $: logLen = log.length;
  $: logTailId = logLen ? log[logLen - 1]?.id : null;
  $: if (visible && (logLen || logTailId != null)) {
    logLen;
    logTailId;
    logScrollPending = true;
    void scrollCombatLog();
  }

  async function scrollCombatLog() {
    await tick();
    // Second tick + rAF: sync scroll can run before lines paint.
    await tick();
    requestAnimationFrame(() => {
      if (logEl) logEl.scrollTop = logEl.scrollHeight;
      logScrollPending = false;
    });
  }

  afterUpdate(() => {
    if (!logScrollPending || !logEl) return;
    logEl.scrollTop = logEl.scrollHeight;
    logScrollPending = false;
  });

  function startTick() {
    if (tickTimer) return;
    tickTimer = setInterval(() => { nowMs = Date.now(); }, 200);
  }
  function stopTick() {
    if (tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  }

  onDestroy(() => {
    unsubDock();
    stopTick();
    stopLootReveal();
    if (bannerTimer) clearTimeout(bannerTimer);
    if (arenaFlashTimer) clearTimeout(arenaFlashTimer);
  });

  function statusEffectsOf(c) {
    const list = (c && c.statusEffects) || [];
    return Array.isArray(list) ? list.filter((se) => se && (se.duration > 0 || se.name)) : [];
  }

  function buffIconUrl(se) {
    const skillId = se?.skillId || se?.id || '';
    try {
      return skillGenericArtUrl(skillId) || '';
    } catch (_) {
      return '';
    }
  }

  function buffTitle(se) {
    const name = se?.name || skillDisplayName(se?.skillId || se?.id) || 'Effect';
    const rounds = se?.duration != null ? `${se.duration} round${se.duration === 1 ? '' : 's'}` : '';
    const kind = se?.type ? String(se.type) : 'buff';
    return rounds ? `${name} (${kind}) · ${rounds}` : `${name} (${kind})`;
  }

  function buffKindClass(se) {
    const t = String(se?.type || 'buff').toLowerCase();
    if (t === 'debuff' || t === 'dot' || t === 'stun') return t;
    if (t === 'hot') return 'hot';
    return 'buff';
  }

  function combatantPortrait(c) {
    if (!c) return enemySilhouette();
    const mineId = (selfCombatant && selfCombatant.id) || selfId;
    const isPlayer = c.type === 'player' || (players || []).some((player) => player.id === c.id);
    return battleSpriteSrc(c, {
      isPlayer,
      selfId: mineId,
      character: c.id === mineId ? character : null,
    });
  }

  function hpPct(hp, maxHp) {
    const m = maxHp > 0 ? maxHp : 1;
    return Math.max(0, Math.min(100, (hp / m) * 100));
  }

  function hpColor(pct) {
    if (pct > 60) return '#ef4444'; // enemy bars stay red per mock
    if (pct > 30) return '#f59e0b';
    return '#dc2626';
  }

  function playerHpColor(pct) {
    if (pct > 60) return '#22c55e';
    if (pct > 30) return '#f59e0b';
    return '#ef4444';
  }

  function selectEnemy(enemy) {
    // C3: tap portrait/sprite retargets only; Attack dock queues the hit
    if (!enemy || phase !== 'active') return;
    if ((enemy.hp ?? 0) <= 0) return;
    if (enemy.id === targetId) return;
    if (store.setCombatTarget) store.setCombatTarget(enemy.id);
    if (sendMessage) sendMessage(`focus ${enemy.id}`);
  }

  function cmd(text) {
    panel = null;
    battleDockOpen.set(false);
    if (sendMessage && text) sendMessage(text);
  }

  const unsubDock = battleDockOpen.subscribe((open) => {
    if (!open && panel === 'items') panel = null;
  });

  function doAttack() {
    const target = livingFocus(enemies, targetId);
    if (target?.id) cmd(`attack ${target.id}`);
    else cmd('attack');
  }

  function doDefend() { cmd('defend'); }
  function doFlee() { cmd('flee'); }

  let boltSpent = false;
  let rigSpent = false;
  let fightOpen = false;
  $: {
    const open = phase === 'active';
    if (open !== fightOpen) {
      fightOpen = open;
      boltSpent = false;
      rigSpent = false;
    }
  }
  $: scrapClass = ($classCatalog.source, armsScrap(characterClassId(character) || selfCombatant?.classId || selfClass));

  function doBolt() {
    if (boltSpent || !scrapClass) return;
    boltSpent = true;
    const target = livingFocus(enemies, targetId);
    if (target?.id) cmd(`bolt ${target.id}`);
    else cmd('bolt');
  }

  function doRig() {
    if (rigSpent || !scrapClass) return;
    rigSpent = true;
    cmd('rig');
  }

  /** When true, Attack/Defend/Items reappear on the left combat rail. Default off to declutter fights. */
  const showCombatUtilityRail = false;


  function queuedChipLabel(action, skillId) {
    if (!action) return '';
    if (action === 'skill') return skillDisplayName(skillId) || 'Skill';
    if (action === 'attack') return 'Attack';
    if (action === 'defend') return 'Defend';
    if (action === 'flee') return 'Flee';
    if (action === 'bolt') return 'Bolt';
    if (action === 'rig') return 'Rig';
    if (action === 'item') return 'Item';
    return String(action);
  }

  function skillCooldownRounds(bind) {
    return cdClock.roundsFor(bind);
  }

  function skillCooldownSec(bind) {
    return cdClock.secondsFor(bind, nowMs);
  }

  function parseOutcomeRewards(msg) {
    const text = String(msg || '');
    const xp = text.match(/\+\s*(\d+)\s*XP/i);
    const gold = text.match(/\+\s*(\d+)\s*Gold/i);
    const defeated = [...text.matchAll(/Defeated:\s*(.+)/gi)].map((m) => m[1].trim()).filter(Boolean);
    // Short summary: first non-banner line
    const lines = text.split(/\n+/).map((l) => l.trim()).filter((l) => l && !/^[=═]+$/.test(l) && !/^(VICTORY|DEFEAT|ESCAPED|REWARDS|LOOT|COMBAT)/i.test(l));
    const summary = lines.slice(0, 3).join(' · ');
    return {
      xp: xp ? Number(xp[1]) : 0,
      gold: gold ? Number(gold[1]) : 0,
      defeated,
      summary: summary || text.slice(0, 160),
    };
  }

  function dismissOutcome() {
    if (store.dismissCombat) store.dismissCombat();
    else if (store.clearCombat) store.clearCombat();
  }

  $: outcomeRewards = parseOutcomeRewards(endMessage);

  function castSkill(skill) {
    if (!skill?.name) return;
    cmd(`cast ${skill.name}`);
  }

  function useItem(item) {
    if (!item?.name) return;
    cmd(`use ${item.name}`);
  }


  function isCombatLogNoise(text) {
    const t = String(text || '').trim();
    if (!t) return true;
    if (/COMBAT STATUS/i.test(t)) return true;
    if (/^TURN ORDER:?\s*$/i.test(t)) return true;
    if (/TURN ORDER/i.test(t) && (/[═=]{6,}/.test(t) || t.split(/\n/).length > 2)) return true;
    if (/Commands:\s*attack/i.test(t) && t.length > 60) return true;
    const bars = (t.match(/[█▓▒░■□▬▭▆▅▃▂▁]/g) || []).length;
    if (bars >= 6) return true;
    const rules = (t.match(/[═]/g) || []).length;
    if (rules >= 8) return true;
    // Multi-line ASCII status / help dumps
    if (t.includes('\n') && t.length > 140 && /\bHP\b|TURN ORDER|COMBAT STATUS|Commands:/i.test(t)) {
      return true;
    }
    // Mostly box-drawing / separator lines
    const lines = t.split(/\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length >= 3) {
      const noisy = lines.filter((l) => /^[═=\-_|\s♦]+$/.test(l) || /█|▓|░|\[={0,1}-{2,}={0,1}\]/.test(l)).length;
      if (noisy >= Math.ceil(lines.length * 0.5)) return true;
    }
    return false;
  }

  function bannerIconFor(kind) {
    switch (kind) {
      case 'crit': return 'whatshot';
      case 'hit': return 'flash_on';
      case 'heal': return 'favorite';
      case 'miss': return 'blur_on';
      case 'defend': return 'security';
      case 'flee': return 'directions_run';
      case 'cast': return 'auto_fix_high';
      default: return 'campaign';
    }
  }

  function makeBannerParts({ kind = 'other', actor = '', verb = '', target = '', amount = 0, raw = '' } = {}) {
    const k = kind || 'other';
    return {
      kind: k,
      actor: actor || '',
      verb: verb || '',
      target: target || '',
      amount: Number(amount) || 0,
      icon: bannerIconFor(k),
      raw: raw || '',
    };
  }

  function formatFxBannerParts(fxEvt) {
    if (!fxEvt) return null;
    const all = [...(players || []), ...(enemies || [])];
    const actor = all.find((c) => c.id === fxEvt.actorId);
    const target = all.find((c) => c.id === fxEvt.targetId);
    const actorName = actor?.name || 'Someone';
    const targetName = target?.name || 'target';
    const dmg = Number(fxEvt.damage) || 0;
    const heal = Number(fxEvt.heal) || 0;
    const result = String(fxEvt.result || '').toLowerCase();
    const fxId = String(fxEvt.fxId || '').toLowerCase();
    const action = String(fxEvt.action || '').trim();
    if (dmg > 0) {
      const crit = result === 'crit';
      const verb = crit ? 'crits' : 'hits';
      const raw = `${actorName} ${verb} ${targetName} for ${dmg}`;
      return makeBannerParts({
        kind: crit ? 'crit' : 'hit',
        actor: actorName,
        verb,
        target: targetName,
        amount: dmg,
        raw,
      });
    }
    if (heal > 0) {
      const raw = `${actorName} heals ${targetName} for ${heal}`;
      return makeBannerParts({
        kind: 'heal', actor: actorName, verb: 'heals', target: targetName, amount: heal, raw,
      });
    }
    if (fxId === 'miss' || result === 'miss' || result === 'dodged') {
      const raw = `${actorName} misses ${targetName}`;
      return makeBannerParts({
        kind: 'miss', actor: actorName, verb: 'misses', target: targetName, raw,
      });
    }
    if (fxId === 'defend' || result === 'defended' || result === 'block') {
      const raw = `${actorName} defends`;
      return makeBannerParts({ kind: 'defend', actor: actorName, verb: 'defends', raw });
    }
    if (fxId === 'flee' || result === 'fled') {
      const raw = `${actorName} flees`;
      return makeBannerParts({ kind: 'flee', actor: actorName, verb: 'flees', raw });
    }
    if (fxId === 'cast' || result === 'cast') {
      const raw = action ? `${actorName} casts ${action}` : `${actorName} uses a skill`;
      return makeBannerParts({
        kind: 'cast', actor: actorName, verb: action ? 'casts' : 'uses', target: action || 'a skill', raw,
      });
    }
    if (action) {
      const raw = `${actorName} ${action}`;
      return makeBannerParts({ kind: 'other', actor: actorName, verb: action, raw });
    }
    return null;
  }

  function formatFxBanner(fxEvt) {
    const parts = formatFxBannerParts(fxEvt);
    return parts?.raw || '';
  }

  function hotbarSlotTitle(bind) {
    if (!bind) return 'Empty';
    if (bind.kind === 'skill') {
      const name = bind.name || skillDisplayName(bind.id);
      const sec = skillCooldownSec(bind);
      const rounds = skillCooldownRounds(bind);
      if (sec > 0) {
        const turns = rounds > 0 ? ` (~${rounds} turn${rounds === 1 ? '' : 's'})` : '';
        return `${name} — ${sec}s CD${turns}`;
      }
      return `Cast ${name}`;
    }
    if (bind.kind === 'item') {
      const item = findInventoryItem(inventory, bind);
      return item ? `Use ${item.name}` : `${bind.name || 'Item'} (missing)`;
    }
    if (bind.kind === 'action') return bind.name || bind.id || 'Action';
    return 'Empty';
  }

  function hotbarSlotDisabled(bind) {
    if (!bind) return true;
    if (bind.kind === 'item' && !findInventoryItem(inventory, bind)) return true;
    if (bind.kind === 'skill' && skillCooldownSec(bind) > 0) return true;
    return false;
  }

  function activateHotbarSlot(bind) {
    if (!bind) return;
    const result = resolveHotbarActivation(bind, { inCombat: true, inventory });
    if (!result.ok) {
      if (result.reason && result.reason !== 'empty' && overlayStore?.pushMessage) {
        overlayStore.pushMessage(result.reason);
      }
      return;
    }
    if (result.command) cmd(result.command);
  }

  function togglePanel(name) {
    panel = panel === name ? null : name;
    battleDockOpen.set(panel === 'items');
  }

  function toggleLogExpanded() {
    logExpanded = !logExpanded;
    logScrollPending = true;
    void scrollCombatLog();
  }

  function outcomeLabel(o) {
    switch (o) {
      case 'victory': return 'Victory';
      case 'defeat': return 'Defeat';
      case 'fled': return 'Fled';
      case 'timeout': return 'Combat Ended';
      default: return o || 'Combat Ended';
    }
  }

  function isFxTarget(id) {
    return fxActive && id && fx && fx.targetId === id;
  }
  function isFxActor(id) {
    return fxActive && id && fx && fx.actorId === id;
  }
  function showFloatOn(id) {
    return isFxTarget(id) && (fxDamage > 0 || fxHeal > 0 || fxIsMiss);
  }

  function onImgError(ev, who, className = selfClass) {
    const img = ev && ev.currentTarget;
    if (!img || img.dataset.fallback === '1') return;
    img.dataset.fallback = '1';
    const next = who === 'player' ? playerSilhouette(className) : enemySilhouette();
    if (img.getAttribute('src') !== next) img.src = next;
  }
</script>

<svelte:window on:keydown={onOutcomeKey} />
{#if visible}
<div class="battle-stage" class:ending={phase === 'ending'} class:layout-b={layoutB} class:reduced-motion={prefersReducedMotion($settingsStore.interface?.reducedMotion)} role="dialog" aria-label="Combat" tabindex="-1" use:combatStageFocus>
  <div class="battle-backdrop" aria-hidden="true"></div>
  <div class="battle-frame">
  <header class="battle-header">
    <i class="material-icons header-icon" aria-hidden="true">explore</i>
    <span class="header-label">COMBAT</span>
    {#if layoutB}
      <span class="round-chip layout-b-chip" title="Battle layout B — Classic in Settings, or ?battleLayout=classic">Layout B</span>
    {/if}
    {#if turn?.round}
      <span class="round-chip">Round {turn.round}</span>
    {/if}
    {#if (turn?.actorName || showWaitingTimer) && (!layoutB || decisionActive)}
      <span class="turn-chip" class:waiting={showWaitingTimer}>
        {#if decisionActive}
          Your turn
        {:else if waitingOnBeat}
          Waiting…
        {:else if isMyTurn}
          Resolving…
        {:else}
          {turn?.actorName ? `${turn.actorName}'s turn` : 'Waiting…'}
        {/if}
      </span>
    {/if}
    <div class="header-rule"></div>
  </header>

  <div
    class="decision-timer"
    class:idle={!decisionActive && !showWaitingTimer}
    class:waiting={showWaitingTimer}
    class:my-turn={decisionActive}
    title={decisionActive ? `Decision window — ${timerSec}s` : (showWaitingTimer ? waitingLabel : 'Decision window')}
    aria-live="polite"
    aria-hidden={!decisionActive && !showWaitingTimer}
  >
    {#if decisionActive}
      <div class="decision-timer-fill" style="width: {timerPct}%"></div>
      <span class="decision-timer-label">{timerSec}s</span>
    {:else if showWaitingTimer}
      <div class="decision-timer-fill wait-pulse" style={waitingOnBeat ? `width: ${Math.max(8, Math.min(100, (beatLeftMs / DEFAULT_BEAT_BUDGET_MS) * 100))}%` : ''}></div>
      <span class="decision-timer-label">{waitingLabel}</span>
    {/if}
  </div>

  <!-- Arena band: fighters + FX + short action banner (floats clip here) -->
  <div
    class="battle-arena"
    class:foes-solo={enemyPack === 'solo'}
    class:foes-duo={enemyPack === 'duo'}
    class:foes-swarm={enemyPack === 'swarm'}
  >
  <div
    class="arena-art"
    class:has-art={!!arenaBgUrl}
    style={arenaBgUrl ? `background-image: url('${arenaBgUrl}')` : ''}
    aria-hidden="true"
  ></div>
  <div class="arena-vignette" aria-hidden="true"></div>
  {#if threatWarning && nowMs - threatWarning.at < 3200}
    <div class="focus-threat-warning threat-{threatWarning.tier}" role="alert">{threatWarning.text}</div>
  {/if}

  {#if layoutB}
    <aside class="layout-b-frame player-frame" aria-label="Player details">
      <div class="lb-frame-head">
        <div
          class="lb-name"
          class:hit-flash={isFxTarget(selfId) && (fxIsHit || fxIsMiss) && !fxIsCrit && !fxIsCrush}
          class:crit-flash={isFxTarget(selfId) && fxIsCrit && !fxIsCrush}
          class:crush-flash={isFxTarget(selfId) && fxIsCrush}
        >{selfName}</div>
      </div>
      <div class="hp-row lb-hp">
        <span class="hp-label">HP</span>
        <div class="hp-track">
          <div
            class="hp-fill"
            style="width: {hpPct(selfHp, selfMaxHp)}%; background: {playerHpColor(hpPct(selfHp, selfMaxHp))}"
          ></div>
        </div>
        <span class="hp-nums">{selfHp} / {selfMaxHp}</span>
      </div>
      {#if selfMaxMana > 0}
        <div class="hp-row lb-mp">
          <span class="hp-label">MP</span>
          <div class="hp-track">
            <div class="hp-fill mp" style="width: {hpPct(selfMana, selfMaxMana)}%"></div>
          </div>
          <span class="hp-nums">{selfMana} / {selfMaxMana}</span>
        </div>
      {/if}
      {#if statusEffectsOf(selfCombatant).length}
        <div class="buff-row lb-buffs" aria-label="Your buffs">
          {#each statusEffectsOf(selfCombatant) as se (se.id || se.name + '-' + se.duration)}
            <div class="buff-icon {buffKindClass(se)}" title={buffTitle(se)}>
              {#if buffIconUrl(se)}
                <img src={buffIconUrl(se)} alt="" />
              {:else}
                <span class="buff-fallback">{(se.name || '?').slice(0, 1)}</span>
              {/if}
              <span class="buff-stacks">{se.duration}</span>
            </div>
          {/each}
        </div>
      {/if}
      {#if decisionActive}
        <div class="status-chips lb-chips">
          <span class="chip focus-chip"><i class="material-icons">flare</i> Focused</span>
        </div>
      {/if}
    </aside>
    {#if focusedEnemy}
      {@const fePct = hpPct(focusedEnemy.hp, focusedEnemy.maxHp)}
      {@const feDead = (focusedEnemy.hp ?? 0) <= 0}
      <aside class="layout-b-frame target-frame" aria-label="Target details">
        <div
          class="lb-name"
          class:threat-grey={focusedEnemy.threat === 'grey'}
          class:threat-green={focusedEnemy.threat === 'green'}
          class:threat-yellow={focusedEnemy.threat === 'yellow'}
          class:threat-orange={focusedEnemy.threat === 'orange'}
          class:threat-red={focusedEnemy.threat === 'red'}
          class:threat-skull={focusedEnemy.threat === 'skull'}
        >{#if focusedEnemy.threat === 'skull'}<span class="skull-mark" title="Skull" aria-hidden="true">☠</span>{/if}{focusedEnemy.name}{#if enemyCount > 1 && !feDead}<span class="enemy-focus-label">FOCUS</span>{/if}</div>
        {#if focusedEnemy.bossPhase}
          <div class="boss-phase-label" aria-label="Boss phase">{phaseCaption(focusedEnemy)}</div>
        {/if}
        <div class="hp-row lb-hp">
          <span class="hp-label">HP</span>
          <div class="hp-track">
            <div class="hp-fill" style="width: {fePct}%; background: {hpColor(fePct)}"></div>
          </div>
          <span class="hp-nums">{focusedEnemy.hp ?? 0} / {focusedEnemy.maxHp ?? 0}</span>
        </div>
        {#if statusEffectsOf(focusedEnemy).length}
          <div class="buff-row lb-buffs" aria-label="Target effects">
            {#each statusEffectsOf(focusedEnemy) as se (se.id || se.name + '-' + se.duration)}
              <div class="buff-icon {buffKindClass(se)}" title={buffTitle(se)}>
                {#if buffIconUrl(se)}
                  <img src={buffIconUrl(se)} alt="" />
                {:else}
                  <span class="buff-fallback">{(se.name || '?').slice(0, 1)}</span>
                {/if}
                <span class="buff-stacks">{se.duration}</span>
              </div>
            {/each}
          </div>
        {/if}
      </aside>
    {/if}
  {/if}

  <!-- Enemies upper-right -->
  <section
    class="enemy-strip"
    class:pack-solo={enemyPack === 'solo'}
    class:pack-duo={enemyPack === 'duo'}
    class:pack-swarm={enemyPack === 'swarm'}
    data-count={enemyCount}
    aria-label="Enemies"
  >
    {#if phaseNotice}
      <div class="phase-banner" role="status">{phaseNotice}</div>
    {/if}
    {#if windupText}
      <div class="telegraph-banner" role="status">{windupText}</div>
    {/if}
    {#each enemies as enemy (enemy.id)}
      {@const pct = hpPct(enemy.hp, enemy.maxHp)}
      {@const dead = (enemy.hp ?? 0) <= 0}
      {@const tgt = isFxTarget(enemy.id)}
      {@const act = isFxActor(enemy.id)}
      <button
        type="button"
        class="enemy-card"
        class:targeted={enemy.id === targetId}
        class:dead
        class:fx-hit={tgt && fxIsHit}
        class:fx-crit={tgt && fxIsCrit}
        class:fx-miss={tgt && fxIsMiss}
        class:fx-death={tgt && fxIsDeath}
        class:fx-cast={act && fxIsCast}
        disabled={dead || phase !== 'active'}
        aria-pressed={enemy.id === targetId}
        aria-label={`Target ${enemy.name || 'enemy'}`}
        on:click={() => selectEnemy(enemy)}
      >
        <div class="foe-plate">
          <div
            class="nameplate"
            class:threat-grey={enemy.threat === 'grey'}
            class:threat-green={enemy.threat === 'green'}
            class:threat-yellow={enemy.threat === 'yellow'}
            class:threat-orange={enemy.threat === 'orange'}
            class:threat-red={enemy.threat === 'red'}
            class:threat-skull={enemy.threat === 'skull'}
            class:winding={!!enemy.telegraph}
            class:enraged={!!enemy.enraged}
            class:hit-flash={tgt && (fxIsHit || fxIsMiss) && !fxIsCrit && !fxIsCrush}
            class:crit-flash={tgt && fxIsCrit && !fxIsCrush}
            class:crush-flash={tgt && fxIsCrush}
          >{#if enemy.threat === 'skull'}<span class="skull-mark" title="Skull" aria-hidden="true">☠</span>{/if}{enemy.name}{#if enemy.id === targetId && enemyCount > 1 && !dead}<span class="enemy-focus-label">FOCUS</span>{/if}{#if enemy.enraged}<span class="enrage-badge">Enraged</span>{/if}</div>
          {#if enemy.bossPhase}
            <div class="boss-phase-label" aria-label="Boss phase">{phaseCaption(enemy)}</div>
          {/if}

          <div class="hp-row">
            <span class="hp-label">HP</span>
            <div class="hp-track">
              <div class="hp-fill" style="width: {pct}%; background: {hpColor(pct)}"></div>
            </div>
            <span class="hp-nums">{enemy.hp ?? 0} / {enemy.maxHp ?? 0}</span>
          </div>
        </div>
        <div class="enemy-sprite-wrap" class:shake={tgt && fxIsHit}>
          {#if statusEffectsOf(enemy).length}
            <div class="buff-row portrait-buffs" aria-label="Enemy effects">
              {#each statusEffectsOf(enemy) as se (se.id || se.name + '-' + se.duration)}
                <div class="buff-icon {buffKindClass(se)}" title={buffTitle(se)}>
                  {#if buffIconUrl(se)}
                    <img src={buffIconUrl(se)} alt="" />
                  {:else}
                    <span class="buff-fallback">{(se.name || '?').slice(0, 1)}</span>
                  {/if}
                  <span class="buff-stacks">{se.duration}</span>
                </div>
              {/each}
            </div>
          {/if}
          <div
            class="ground-marker target-ring"
            class:focus={enemy.id === targetId && !dead}
            class:dead={dead}
            aria-hidden="true"
          ></div>
          {#if layoutB}
            <div class="sprite-hp" aria-hidden="true">
              <div class="sprite-hp-track"><div class="sprite-hp-fill" style="width: {pct}%; background: {hpColor(pct)}"></div></div>
            </div>
          {/if}
          <img
            class="enemy-sprite"
            src={combatantPortrait(enemy)}
            alt=""
            on:error={(e) => onImgError(e, 'enemy')}
          />
          {#if dead}<span class="defeated-label">Defeated</span>{/if}
          {#if showFloatOn(enemy.id)}
            <div class="fx-float over-sprite" data-key={fxKey}>
              {#if fxIsMiss}
                <span class="fx-miss-label">miss</span>
              {:else if fxDamage > 0}
                <span class="fx-dmg" class:crit={fxIsCrit} class:crush={fxIsCrush}>-{fxDamage}</span>
              {:else if fxHeal > 0}
                <span class="fx-heal">+{fxHeal}</span>
              {/if}
            </div>
          {/if}
          {#if tgt && fxIsMiss}
            <div class="fx-puff" data-key={fxKey} aria-hidden="true"></div>
          {/if}
          {#if tgt && fxIsHit && !fxIsMiss}
            <div class="fx-slash" class:crit={fxIsCrit || fxIsCrush} data-key={fxKey} aria-hidden="true"></div>
          {/if}
        </div>
      </button>
    {/each}
  </section>

  <!-- Center FX burst (cast / stage-level accent) -->
  <div class="fx-layer" aria-hidden="true">
    {#if fxActive && (fxIsCast || fxIsDefend || fxIsFlee)}
      <div class="fx-burst fx-{fxId || 'cast'}" data-key={fxKey}>
        {#if fxIsDefend}
          <span class="fx-burst-label">Defend</span>
        {:else if fxIsFlee}
          <span class="fx-burst-label">Flee</span>
        {:else if fxIsCast && fxHeal <= 0 && fxDamage <= 0}
          <span class="fx-burst-label">Cast</span>
        {/if}
      </div>
    {/if}
  </div>

  {#if showJoinBanner}
    <div class="join-banner" role="status">{combatJoin.actorName} joins the fight!</div>
  {/if}

  <div class="player-team" class:solo={allies.length === 0}>
  {#if allies.length}
    <section class="ally-strip" aria-label="Allies">
      {#each allies as ally (ally.id)}
        {@const pct = hpPct(ally.hp, ally.maxHp)}
        {@const down = !ally.isAlive || (ally.hp ?? 0) <= 0}
        {@const tgt = isFxTarget(ally.id)}
        <div
          class="ally-card"
          class:ally-turn={turn?.actorId === ally.id}
          class:ally-down={down}
          class:ally-fled={ally.hasFled}
          class:ally-hit={tgt && fxIsHit}
          class:ally-crit={tgt && fxIsCrit}
          class:just-joined={showJoinBanner && combatJoin.actorId === ally.id}
        >
          <div class="ally-portrait">
            {#if statusEffectsOf(ally).length}
              <div class="buff-row portrait-buffs ally" aria-label="Ally effects">
                {#each statusEffectsOf(ally) as se (se.id || se.name + '-' + se.duration)}
                  <div class="buff-icon {buffKindClass(se)}" title={buffTitle(se)}>
                    {#if buffIconUrl(se)}
                      <img src={buffIconUrl(se)} alt="" />
                    {:else}
                      <span class="buff-fallback">{(se.name || '?').slice(0, 1)}</span>
                    {/if}
                    <span class="buff-stacks">{se.duration}</span>
                  </div>
                {/each}
              </div>
            {/if}
            <img src={combatantPortrait(ally)} alt="" on:error={(e) => onImgError(e, 'player', ally.classId)} />
            {#if showFloatOn(ally.id)}
              <div class="fx-float over-sprite" data-key={fxKey}>
                {#if fxIsMiss}<span class="fx-miss-label">miss</span>
                {:else if fxDamage > 0}<span class="fx-dmg" class:crit={fxIsCrit} class:crush={fxIsCrush}>-{fxDamage}</span>
                {:else if fxHeal > 0}<span class="fx-heal">+{fxHeal}</span>{/if}
              </div>
            {/if}
          </div>
          <div class="ally-meta">
            <div class="ally-name" class:hit-flash={tgt && fxIsHit}>{ally.name}</div>

            <div class="ally-sub">{ally.classId || 'Adventurer'} · Lv {ally.level || 1}{#if down} · Down{:else if ally.hasFled} · Fled{:else if turn?.actorId === ally.id} · Turn{/if}</div>
            <div class="ally-bar"><span style="width: {pct}%; background: {playerHpColor(pct)}"></span></div>
            <div class="ally-numbers">HP {ally.hp ?? 0}/{ally.maxHp ?? 0}</div>
            {#if ally.maxMana > 0}
              <div class="ally-bar mana"><span style="width: {hpPct(ally.mana, ally.maxMana)}%"></span></div>
              <div class="ally-numbers">MP {ally.mana ?? 0}/{ally.maxMana}</div>
            {/if}
          </div>
        </div>
      {/each}
    </section>
  {/if}

  <!-- Player lower-left -->
  <section
    class="player-panel"
    class:fx-hit={isFxTarget(selfId) && fxIsHit}
    class:fx-miss={isFxTarget(selfId) && fxIsMiss}
    class:fx-death={isFxTarget(selfId) && fxIsDeath}
    class:fx-cast={isFxActor(selfId) && (fxIsCast || fxIsDefend)}
    class:fx-defend={isFxActor(selfId) && fxIsDefend}
    aria-label="Player"
  >
    <div class="player-bust" class:shake={isFxTarget(selfId) && fxIsHit}>
      {#if statusEffectsOf(selfCombatant).length}
        <div class="buff-row portrait-buffs player" aria-label="Your buffs">
          {#each statusEffectsOf(selfCombatant) as se (se.id || se.name + '-' + se.duration)}
            <div class="buff-icon {buffKindClass(se)}" title={buffTitle(se)}>
              {#if buffIconUrl(se)}
                <img src={buffIconUrl(se)} alt="" />
              {:else}
                <span class="buff-fallback">{(se.name || '?').slice(0, 1)}</span>
              {/if}
              <span class="buff-stacks">{se.duration}</span>
            </div>
          {/each}
        </div>
      {/if}
      <div class="ground-marker player-marker" class:focus={decisionActive} aria-hidden="true"></div>
      {#if layoutB}
        <div class="sprite-hp player-sprite-hp" aria-hidden="true">
          <div class="sprite-hp-track"><div class="sprite-hp-fill" style="width: {hpPct(selfHp, selfMaxHp)}%; background: {playerHpColor(hpPct(selfHp, selfMaxHp))}"></div></div>
        </div>
      {/if}
      <img
        class="player-sprite"
        src={combatantPortrait(selfCombatant)}
        alt=""
        on:error={(e) => onImgError(e, 'player')}
      />
      {#if showFloatOn(selfId)}
        <div class="fx-float over-sprite" data-key={fxKey}>
          {#if fxIsMiss}<span class="fx-miss-label">miss</span>
          {:else if fxDamage > 0}<span class="fx-dmg" class:crit={fxIsCrit} class:crush={fxIsCrush}>-{fxDamage}</span>
          {:else if fxHeal > 0}<span class="fx-heal">+{fxHeal}</span>{/if}
        </div>
      {/if}
      {#if isFxTarget(selfId) && fxIsMiss}
        <div class="fx-puff" data-key={fxKey} aria-hidden="true"></div>
      {/if}
      {#if isFxActor(selfId) && fxIsCast}
        <div class="fx-cast-glow" data-key={fxKey} aria-hidden="true"></div>
      {/if}
      {#if isFxActor(selfId) && fxIsDefend}
        <div class="fx-shield" data-key={fxKey} aria-hidden="true"></div>
      {/if}
    </div>
    <div class="player-meta">
      <div
        class="player-name"
        class:hit-flash={isFxTarget(selfId) && (fxIsHit || fxIsMiss) && !fxIsCrit && !fxIsCrush}
        class:crit-flash={isFxTarget(selfId) && fxIsCrit && !fxIsCrush}
        class:crush-flash={isFxTarget(selfId) && fxIsCrush}
      >{selfName}
      </div>

      <div class="hp-row player-hp">
        <span class="hp-label">HP</span>
        <div class="hp-track">
          <div
            class="hp-fill"
            style="width: {hpPct(selfHp, selfMaxHp)}%; background: {playerHpColor(hpPct(selfHp, selfMaxHp))}"
          ></div>
        </div>
        <span class="hp-nums">{selfHp} / {selfMaxHp}</span>
      </div>
      {#if selfMaxMana > 0}
        <div class="hp-row player-mp">
          <span class="hp-label">MP</span>
          <div class="hp-track">
            <div class="hp-fill mp" style="width: {hpPct(selfMana, selfMaxMana)}%"></div>
          </div>
          <span class="hp-nums">{selfMana} / {selfMaxMana}</span>
        </div>
      {/if}
      <div class="status-chips">
        {#if selfClass}
          <span class="chip class-chip"><i class="material-icons">military_tech</i> {selfClass}</span>
        {/if}
        {#if decisionActive}
          <span class="chip focus-chip"><i class="material-icons">flare</i> Focused</span>
        {:else if showWaitingTimer}
          <span class="chip wait-chip status-pill"><i class="material-icons spin-slow">hourglass_top</i> {waitingLabel}</span>
        {/if}
      </div>
    </div>
  </section>
  </div>

  {#if arenaFlash}
    <div
      class="arena-hit-flash"
      class:crit={bannerParts?.kind === 'crit'}
      class:heal={bannerParts?.kind === 'heal'}
      aria-hidden="true"
    ></div>
  {/if}

  <div class="action-banner-stack" aria-live="polite">
    {#if bannerVisible && bannerParts && !showJoinBanner}
      <div
        class="action-banner kind-{bannerParts.kind}"
        class:flash={bannerFlashKey > 0}
        data-flash={bannerFlashKey}
      >
        <i class="material-icons banner-icon">{bannerParts.icon}</i>
        <div class="banner-copy">
          {#if bannerParts.actor || bannerParts.verb}
            <span class="banner-actor">{bannerParts.actor}</span>
            {#if bannerParts.verb}
              <span class="banner-verb">{bannerParts.verb}</span>
            {/if}
            {#if bannerParts.target}
              <span class="banner-target">{bannerParts.target}</span>
            {/if}
            {#if bannerParts.amount > 0}
              <span class="banner-for">for</span>
              <span class="banner-amount">{bannerParts.amount}</span>
            {/if}
          {:else}
            <span class="banner-raw">{bannerParts.raw || bannerText}</span>
          {/if}
        </div>
      </div>
    {/if}
  </div>
  </div><!-- /.battle-arena -->

  <!-- Dock: one chrome strip — status chip, then rail + hotbar on one baseline -->
  {#if phase === 'active'}
    <div class="battle-controls">
      <div class="dock-status" class:has-chip={!!((queuedAction && queuedLabel) || (showWaitingTimer && !queuedAction))} aria-live="polite">
        {#if queuedAction && queuedLabel}
          <div class="queued-chip status-pill" title="Queued action">
            <i class="material-icons spin-slow">hourglass_top</i>
            <span class="queued-name">{queuedLabel}</span>
            {#if queueLeftSec > 0}
              <span class="queued-cd">{queueLeftSec}s</span>
            {:else}
              <span class="queued-cd">resolving…</span>
            {/if}
          </div>
        {:else if showWaitingTimer}
          <div class="queued-chip wait status-pill" title={waitingLabel}>
            <i class="material-icons spin-slow">hourglass_top</i>
            <span class="queued-name">{waitingLabel}</span>
          </div>
        {/if}
      </div>

      <div class="dock-main">
        <!-- Combat rail: Flee only. Attack/Defend/Items deferred — hotbar (melee/defend/consumables) + auto-attack cover them. Flip showCombatUtilityRail to restore. -->
        <nav class="battle-rail essential" aria-label="Combat actions">
          {#if showCombatUtilityRail}
            <button type="button" class="rail-btn primary" title="Attack" aria-label="Attack" on:click|stopPropagation={doAttack}>
              <i class="material-icons">flash_on</i>
              <span class="rail-label">Attack</span>
            </button>
            <button type="button" class="rail-btn" title="Defend" aria-label="Defend" on:click|stopPropagation={doDefend}>
              <i class="material-icons">security</i>
              <span class="rail-label">Defend</span>
            </button>
            <button type="button" class="rail-btn" class:active={panel === 'items'} title="Items" aria-label="Items" on:click|stopPropagation={() => togglePanel('items')}>
              <i class="material-icons">shopping_bag</i>
              <span class="rail-label">Items</span>
            </button>
          {/if}
          {#if scrapClass && !boltSpent}
            <button type="button" class="rail-btn scrap" title="Bolt" aria-label="Bolt" on:click|stopPropagation={doBolt}>
              <i class="material-icons">build</i>
              <span class="rail-label">Bolt</span>
            </button>
          {/if}
          {#if scrapClass && !rigSpent}
            <button type="button" class="rail-btn scrap" title="Rig" aria-label="Rig" on:click|stopPropagation={doRig}>
              <i class="material-icons">memory</i>
              <span class="rail-label">Rig</span>
            </button>
          {/if}
          <button type="button" class="rail-btn flee" title="Flee" aria-label="Flee" on:click|stopPropagation={doFlee}>
            <i class="material-icons">directions_run</i>
            <span class="rail-label">Flee</span>
          </button>
        </nav>

        <div class="dock-divider" aria-hidden="true"></div>

        <div class="combat-hotbar" aria-label="Combat hotbar">
          {#each hotbarBinds as bind, index}
            {@const item = bind?.kind === 'item' ? findInventoryItem(inventory, bind) : null}
            {@const cdSec = skillCooldownSec(bind)}
            <button
              type="button"
              class="hb-slot"
              class:filled={!!bind}
              class:skill={bind?.kind === 'skill'}
              class:item={bind?.kind === 'item'}
              class:action={bind?.kind === 'action'}
              class:empty={!bind}
              class:disabled={hotbarSlotDisabled(bind)}
              class:on-cd={cdSec > 0}
              title={hotbarSlotTitle(bind)}
              aria-label={hotbarSlotTitle(bind)}
              disabled={hotbarSlotDisabled(bind)}
              on:click|stopPropagation={() => activateHotbarSlot(bind)}
            >
              <span class="hb-index">{index + 1}</span>
              {#if bind?.kind === 'item'}
                <img
                  src={itemArtSrc(item || { name: bind.name, templateId: bind.id, type: 'consumable' })}
                  alt=""
                  on:error={(e) => onItemArtError(e, item || { type: 'consumable' })}
                />
              {:else if bind?.kind === 'skill'}
                <img
                  src={skillGenericArtUrl(bind.id || bind.name)}
                  alt=""
                  on:error={(e) => onItemArtError(e, { type: 'default' })}
                />
              {:else if bind?.kind === 'action'}
                <img
                  src={actionGenericArtUrl(bind.id)}
                  alt=""
                  on:error={(e) => onItemArtError(e, { type: 'default' })}
                />
              {/if}
              {#if cdSec > 0}
                <span class="hb-cd-overlay" aria-hidden="true">{cdSec}s</span>
              {/if}
            </button>
          {/each}
        </div>
      </div>

    {#if panel === 'items'}
      <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
      <div class="dock-sheet-backdrop" on:click={() => panel = null}></div>
      <div class="dock-panel sheet" role="menu" aria-label="Items">
        <div class="dock-sheet-head">
          <div class="dock-sheet-tabs">
            <button type="button" class="dock-tab active">
              <i class="material-icons">shopping_bag</i> Items
            </button>
          </div>
          <button type="button" class="dock-sheet-close" aria-label="Close" on:click={() => panel = null}>
            <i class="material-icons">close</i>
          </button>
        </div>
        {#if consumables.length === 0}
          <div class="dock-empty">No consumables — bind potions on the hotbar</div>
        {:else}
          {#each consumables as item (item.id || item.name)}
            <button type="button" class="dock-panel-btn" on:click={() => useItem(item)}>
              <i class="material-icons">science</i> {item.name}
            </button>
          {/each}
        {/if}
      </div>
    {/if}
    </div>
  {/if}

  <!-- Combat log — full-width framed panel; mobile peek/expand -->
  <aside
    class="combat-log"
    class:log-expanded={logExpanded}
    class:log-peek={!logExpanded}
    bind:this={logEl}
    aria-label="Combat log"
  >
    <button
      type="button"
      class="combat-log-title"
      aria-expanded={logExpanded}
      aria-controls="combat-log-body"
      on:click={toggleLogExpanded}
    >
      <span>♦ COMBAT LOG</span>
      <i class="material-icons log-expand-icon" aria-hidden="true">{logExpanded ? 'expand_more' : 'unfold_more'}</i>
    </button>
    <div id="combat-log-body" class="combat-log-body">
      {#if log.length}
        {#each log.slice(-10) as line (line.id)}
          {#if line.kind === "combatEvent" && line.hook === "unique"}
            <div class="combat-unique-line">
              <i class="material-icons" aria-hidden="true">diamond</i>
              <span class="unique-tag">Unique</span>
              <span class="combat-unique-name">{uniqueDropName(line.text)}</span>
              {#if line.source}<span class="combat-event-source">{line.source}</span>{/if}
            </div>
          {:else if line.kind === "combatEvent"}
            <div class="combat-event-card">
              <i class="material-icons" aria-hidden="true">{combatEventIcon(line.hook)}</i>
              <span class="combat-event-text">{line.text}</span>
              {#if line.source}<span class="combat-event-source">{line.source}</span>{/if}
            </div>
          {:else}
            <div class="combat-log-line">&gt; {line.text}</div>
          {/if}
        {/each}
      {:else}
        <div class="combat-log-line muted">&gt; Waiting for the clash…</div>
      {/if}
    </div>
  </aside>

  {#if phase === 'ending'}
    <div class="outcome-panel" class:victory={outcome === 'victory'} class:defeat={outcome === 'defeat'} class:fled={outcome === 'fled'}>
      <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
      <div class="outcome-card" role="status" on:click={dismissOutcome}>
        <div class="outcome-ornament" aria-hidden="true">♦</div>
        <div class="outcome-title">{outcomeLabel(outcome)}</div>
        {#if outcome === 'victory' && hasRewardBreakdown}
          <ul class="reward-breakdown">
            <li>Base: {rewardBreakdown.baseXp || 0} XP, {rewardBreakdown.baseGold || 0} gold</li>
            <li>Level modifier (highest in the split, L{rewardBreakdown.referenceLevel || 1}): {signedReward(rewardBreakdown.levelModXp)} XP, {signedReward(rewardBreakdown.levelModGold)} gold</li>
            {#if (rewardBreakdown.firstKillXp || 0) !== 0 || (rewardBreakdown.firstKillGold || 0) !== 0}
              <li>First-kill bonus: {signedReward(rewardBreakdown.firstKillXp)} XP, {signedReward(rewardBreakdown.firstKillGold)} gold</li>
            {/if}
            <li>Your share: {rewardBreakdown.xp || 0} XP, {rewardBreakdown.gold || 0} gold</li>
          </ul>
          {#if (rewardBreakdown.xp || 0) > 0 || (rewardBreakdown.gold || 0) > 0}
            <div class="outcome-rewards">
              {#if rewardBreakdown.xp}
                <span class="reward-chip xp"><i class="material-icons">star</i> +{rewardBreakdown.xp} XP</span>
              {/if}
              {#if rewardBreakdown.gold}
                <span class="reward-chip gold"><i class="material-icons">monetization_on</i> +{rewardBreakdown.gold} Gold</span>
              {/if}
            </div>
          {/if}
          {#if lootList.length}
            <ul class="loot-reveal" aria-label="Loot">
              {#each lootList.slice(0, lootShown) as item, index (`${item.name}-${index}`)}
                {#if item.unique}
                  <li class="loot-item loot-unique">
                    <i class="material-icons loot-unique-gem" aria-hidden="true">diamond</i>
                    <span class="loot-unique-body">
                      <span class="unique-tag">Unique</span>
                      <span class="loot-unique-name">{item.name}{#if item.quantity > 1} ×{item.quantity}{/if}</span>
                    </span>
                  </li>
                {:else}
                  <li class="loot-item rarity-{rarityClass(item.quality)}">{item.name}{#if item.quantity > 1} ×{item.quantity}{/if}</li>
                {/if}
              {/each}
            </ul>
          {/if}
          {#if levelUp && levelReady}
            <div class="level-callout">Level {levelUp.newLevel}</div>
          {/if}
        {:else if outcome === 'defeat' && defeatInfo}
          <ul class="reward-breakdown">
            <li>Lost {defeatInfo.xpLost || 0} XP</li>
            <li>Lost {defeatInfo.goldLost || 0} gold</li>
            {#if defeatInfo.armor && defeatInfo.armor.length}
              <li>Armor battered: {defeatInfo.armor.join(', ')}</li>
            {/if}
            <li>
              {#if defeatInfo.respawnRoom}
                You awaken at {defeatInfo.respawnRoom}
              {:else}
                You stay where you fell
              {/if}
              ({defeatInfo.hp || 0}/{defeatInfo.maxHp || 0} HP)
            </li>
          </ul>
        {:else if outcomeRewards?.summary}
          <div class="outcome-summary">{outcomeRewards.summary}</div>
        {:else if endMessage}
          <div class="outcome-summary">{endMessage}</div>
        {/if}
        <button type="button" class="outcome-continue" on:click|stopPropagation={dismissOutcome}>
          Continue
        </button>
      </div>
    </div>
  {/if}
  </div><!-- /.battle-frame -->
</div>
{/if}

<style>
  .battle-stage {
    position: fixed;
    inset: 0;
    z-index: 9000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.6rem;
    color: #f3f4f6;
    font-family: 'Cinzel', Georgia, serif;
    pointer-events: auto;
    animation: stageIn 0.28s ease-out;
  }

  /* Desktop: named grid rows — header / arena / dock / log (no absolute dock/log) */
  .battle-frame {
    position: relative;
    z-index: 1;
    width: calc(100vw - 1.2rem);
    height: calc(100vh - 1.2rem);
    min-width: 0;
    min-height: 0;
    max-width: calc(100vw - 1.2rem);
    max-height: calc(100vh - 1.2rem);
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      "header"
      "timer"
      "arena"
      "dock"
      "log";
    border: 1.5px solid rgba(212, 164, 74, 0.65);
    border-radius: 14px;
    overflow: hidden;
    box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.65),
      0 0 0 1px rgba(0, 0, 0, 0.4),
      inset 0 0 0 1px rgba(255, 220, 150, 0.1);
    background: #0a0b0e;
  }

  .battle-header { grid-area: header; }
  .decision-timer { grid-area: timer; }
  .battle-arena {
    grid-area: arena;
    position: relative;
    min-height: 0;
    overflow: hidden;
    z-index: 1;
  }
  .battle-controls {
    grid-area: dock;
  }
  .combat-log {
    grid-area: log;
  }

  /* Room arena art — dimmed cover like the C0 mock lane. */
  .arena-art {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-color: #0a0b0e;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: brightness(0.42) saturate(0.75) contrast(1.05);
    transform: scale(1.02);
  }
  .arena-art:not(.has-art) {
    background-image:
      radial-gradient(ellipse at 70% 35%, rgba(60, 35, 22, 0.55), transparent 55%),
      radial-gradient(ellipse at 25% 75%, rgba(25, 35, 55, 0.4), transparent 50%);
  }
  .arena-vignette {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(ellipse at 50% 42%, transparent 22%, rgba(0, 0, 0, 0.45) 62%, rgba(0, 0, 0, 0.82) 100%),
      linear-gradient(to top, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.25) 28%, transparent 48%),
      linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, transparent 22%),
      linear-gradient(to right, rgba(0, 0, 0, 0.35) 0%, transparent 18%, transparent 82%, rgba(0, 0, 0, 0.35) 100%);
  }

  @keyframes stageIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .battle-backdrop {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 70% 30%, rgba(80, 40, 20, 0.35), transparent 55%),
      radial-gradient(ellipse at 20% 80%, rgba(30, 50, 80, 0.25), transparent 50%),
      rgba(4, 5, 8, 0.82);
    backdrop-filter: blur(6px) saturate(70%) brightness(55%);
    -webkit-backdrop-filter: blur(6px) saturate(70%) brightness(55%);
    z-index: 0;
  }

  .battle-header,
  .decision-timer,
  .combat-log,
  .outcome-panel {
    position: relative;
    z-index: 1;
  }

  /* Dock / controls sit above FX floats & banners so actions stay clickable */
  .battle-controls,
  .battle-dock,
  .battle-rail,
  .dock-main,
  .dock-row,
  .queued-chip,
  .dock-panel,
  .dock-sheet-backdrop {
    position: relative;
    z-index: 20;
  }

  .battle-header {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.55rem 0.85rem;
    min-height: 36px;
    letter-spacing: 0.1em;
    font-family: var(--font-display, 'Cinzel', serif);
    text-transform: uppercase;
    background: var(--panel-header-bg, rgba(0, 0, 0, 0.35));
    border-bottom: 1px solid var(--panel-header-border, rgba(180, 130, 60, 0.22));
  }

  .header-icon {
    color: #d4a44a;
    font-size: 1.4rem;
    text-shadow: 0 0 10px rgba(212, 164, 74, 0.35);
  }

  .header-label {
    color: #e8c878;
    font-size: 1.08rem;
    font-weight: 700;
    text-shadow: 0 1px 6px rgba(0, 0, 0, 0.65);
  }

  .round-chip,
  .turn-chip {
    font-family: system-ui, sans-serif;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    padding: 0.18rem 0.6rem;
    border-radius: 999px;
    border: 1px solid rgba(212, 164, 74, 0.55);
    color: #f5e6c0;
    background: rgba(12, 10, 8, 0.78);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.35);
  }
  .turn-chip.waiting {
    border-color: rgba(148, 163, 184, 0.55);
    color: #e5e7eb;
  }

  .header-rule {
    flex: 1;
    height: 1px;
    margin-left: 0.55rem;
    position: relative;
    background: linear-gradient(90deg, rgba(212, 164, 74, 0.75), rgba(212, 164, 74, 0.2), transparent);
  }
  .header-rule::before,
  .header-rule::after {
    content: '♦';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(212, 164, 74, 0.75);
    font-size: 0.55rem;
    line-height: 1;
  }
  .header-rule::before { left: 0; }
  .header-rule::after { left: 28%; opacity: 0.55; }

  .decision-timer {
    margin: 0.25rem 1.25rem 0;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
    border: 1px solid rgba(212, 164, 74, 0.25);
    position: relative;
  }
  .decision-timer.idle {
    height: 0;
    margin: 0;
    border: none;
    background: transparent;
    overflow: hidden;
  }

  .decision-timer-fill {
    height: 100%;
    background: linear-gradient(90deg, #d4a44a, #f59e0b);
    transition: width 0.2s linear;
  }
  .decision-timer.waiting .decision-timer-fill {
    background: linear-gradient(90deg, rgba(148, 163, 184, 0.55), rgba(212, 164, 74, 0.45));
  }
  .decision-timer-fill.wait-pulse {
    width: 100%;
    animation: waitPulse 1.4s ease-in-out infinite;
  }
  @keyframes waitPulse {
    0%, 100% { opacity: 0.45; }
    50% { opacity: 1; }
  }

  .enemy-strip {
    position: absolute;
    top: 0.35rem;
    right: 0.85rem;
    left: auto;
    z-index: 3;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.85rem;
    padding: 0.4rem 0.4rem 0;
    max-width: min(88%, 520px);
    pointer-events: auto;
  }
  .enemy-strip.pack-solo {
    top: 4%;
    right: 5%;
    max-width: min(55%, 387px);
    max-height: 74%;
  }
  .enemy-strip.pack-duo {
    top: 2%;
    right: 3%;
    gap: 1.15rem;
    max-width: min(82%, 780px);
  }
  .enemy-strip.pack-swarm {
    top: 1%;
    right: 1.5%;
    gap: 0.45rem;
    max-width: min(94%, 920px);
  }

  .enemy-card {
    appearance: none;
    background: transparent;
    border: none;
    color: inherit;
    padding: 0;
    /* ~2/3 prior display size — keeps pack-vs-solo ratios, less stretchy upscale */
    width: clamp(100px, 15vmin, 173px);
    cursor: pointer;
    text-align: center;
    font: inherit;
  }
  .enemy-strip.pack-solo .enemy-card {
    width: clamp(173px, 28vmin, 373px);
  }
  .enemy-strip.pack-duo .enemy-card {
    width: clamp(100px, 15vmin, 187px);
  }
  .enemy-strip.pack-swarm .enemy-card {
    width: clamp(59px, 9vmin, 100px);
  }

  .enemy-card:disabled {
    cursor: default;
    opacity: 0.45;
    filter: grayscale(0.6);
  }

  .foe-plate {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0.35rem 0.4rem 0.3rem;
    margin-bottom: 0.2rem;
    border: 1.5px solid rgba(212, 164, 74, 0.5);
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(14, 12, 10, 0.82), rgba(6, 6, 8, 0.7));
    box-shadow:
      0 8px 20px rgba(0, 0, 0, 0.4),
      inset 0 0 0 1px rgba(255, 220, 150, 0.08);
    box-sizing: border-box;
  }

  .nameplate {
    display: inline-block;
    padding: 0.18rem 0.7rem;
    margin-bottom: 0.28rem;
    border: 1.5px solid rgba(212, 164, 74, 0.8);
    border-radius: 3px;
    background: linear-gradient(180deg, rgba(28, 20, 10, 0.92), rgba(8, 6, 4, 0.92));
    color: #f5e6c0;
    font-size: 0.88rem;
    letter-spacing: 0.04em;
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.55),
      inset 0 0 0 1px rgba(255, 220, 150, 0.12),
      0 4px 12px rgba(0, 0, 0, 0.35);
  }

  .nameplate.winding {
    animation: telegraphPulse 1.4s ease-in-out infinite;
    border-color: #fbbf24;
  }

  .nameplate.enraged {
    border-color: #ef4444;
    color: #fecaca;
  }

  .nameplate,
  .player-name {
    position: relative;
  }

  .nameplate.hit-flash,
  .player-name.hit-flash {
    animation: plateHit 0.32s ease-out;
  }
  .nameplate.crit-flash,
  .player-name.crit-flash {
    animation: plateCrit 0.48s ease-out;
  }
  .nameplate.crush-flash,
  .player-name.crush-flash {
    animation: plateCrush 0.55s ease-out;
  }

  @keyframes plateHit {
    0% { box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.95), 0 0 16px rgba(248, 113, 113, 0.75); filter: brightness(1.7); }
    100% { box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.55); filter: none; }
  }
  @keyframes plateCrit {
    0% { transform: scale(1.08); box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.95), 0 0 26px #fbbf24; filter: brightness(1.9); }
    100% { transform: scale(1); filter: none; }
  }
  @keyframes plateCrush {
    0% { transform: scale(1.16); box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.95), 0 0 36px #ef4444; filter: brightness(2.3); }
    45% { transform: scale(0.96); }
    100% { transform: scale(1); filter: none; }
  }

  .enrage-badge {
    margin-left: 0.45rem;
    padding: 0.05rem 0.35rem;
    border-radius: 3px;
    background: rgba(239, 68, 68, 0.85);
    color: #fff;
    font-size: 0.68rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .boss-phase-label {
    color: #fde68a;
    font-size: 0.78rem;
    text-align: center;
    margin: 0.25rem 0;
    letter-spacing: 0.04em;
  }
  .phase-banner {
    flex: 1 0 100%;
    max-width: 100%;
    padding: 0.6rem 0.9rem;
    border: 2px solid #e8c878;
    border-radius: 6px;
    background: rgba(30, 20, 8, 0.96);
    color: #fff1bd;
    font-family: var(--font-display, 'Cinzel', serif);
    text-align: center;
    animation: phaseArrival 400ms ease-out;
  }
  @keyframes phaseArrival {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .telegraph-banner {
    flex: 1 0 100%;
    z-index: 6;
    max-width: 100%;
    padding: 0.45rem 0.9rem;
    border: 1px solid rgba(251, 191, 36, 0.85);
    border-radius: 6px;
    background: rgba(40, 18, 4, 0.92);
    color: #fde68a;
    font-family: var(--font-display, 'Cinzel', serif);
    letter-spacing: 0.06em;
    text-align: center;
    text-transform: uppercase;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  }

  @keyframes telegraphPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.15); }
    50% { box-shadow: 0 0 16px 2px rgba(251, 191, 36, 0.85); }
  }

  .nameplate.threat-grey { color: #9ca3af; }
  .nameplate.threat-green { color: #4ade80; }
  .nameplate.threat-yellow { color: #facc15; }
  .nameplate.threat-orange { color: #fb923c; }
  .nameplate.threat-red { color: #f87171; }
  .nameplate.threat-skull { color: #fecaca; border-color: rgba(248, 113, 113, 0.85); }
  .skull-mark { margin-right: 0.2em; }

  .hp-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 0.35rem;
    margin: 0 auto;
    max-width: 100%;
    width: 100%;
    font-family: system-ui, sans-serif;
  }

  .hp-label {
    color: #d4a44a;
    font-size: 0.68rem;
    font-weight: 700;
  }

  .hp-track {
    height: 8px;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.55);
    border: 1px solid rgba(212, 164, 74, 0.35);
    overflow: hidden;
  }

  .hp-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.35s ease-out;
  }

  .hp-fill.mp {
    background: #3b82f6;
  }

  .hp-nums {
    color: #e5e7eb;
    font-size: 0.68rem;
    white-space: nowrap;
  }

  .enemy-sprite-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    display: grid;
    place-items: center;
  }

  .enemy-sprite {
    position: relative;
    width: 86%;
    height: 86%;
    object-fit: contain;
    image-rendering: -moz-crisp-edges;
    image-rendering: crisp-edges;
    image-rendering: pixelated;
    -ms-interpolation-mode: nearest-neighbor;
    filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.55));
    z-index: 1;
  }

  /* Soft ground puddle under feet — classic: only the focused target */
  .ground-marker,
  .target-ring {
    position: absolute;
    bottom: 2%;
    left: 50%;
    width: 78%;
    height: 22%;
    transform: translateX(-50%);
    border: none;
    border-radius: 50%;
    background: radial-gradient(
      ellipse at center,
      rgba(250, 204, 21, 0.5) 0%,
      rgba(234, 179, 8, 0.32) 28%,
      rgba(212, 164, 74, 0.14) 55%,
      rgba(250, 204, 21, 0.04) 75%,
      transparent 100%
    );
    box-shadow: none;
    pointer-events: none;
    z-index: 0;
    opacity: 0;
  }
  .ground-marker.focus,
  .target-ring.focus {
    opacity: 1;
  }
  .ground-marker.dead {
    opacity: 0 !important;
  }
  .player-marker {
    display: none; /* classic: no player ground disc */
  }

  .enemy-card.targeted .foe-plate {
    border-color: rgba(250, 204, 21, 0.75);
  }
  .focus-threat-warning {
    position: absolute;
    top: 1.1rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 5;
    max-width: calc(100% - 1rem);
    padding: 0.45rem 0.8rem;
    border: 1px solid currentColor;
    border-radius: 0.35rem;
    background: rgba(20, 13, 11, 0.95);
    color: #fb923c;
    font-weight: 700;
    text-align: center;
    pointer-events: none;
  }
  .focus-threat-warning.threat-red { color: #f87171; }
  .focus-threat-warning.threat-skull { color: #fecaca; }
  .enemy-card.targeted .nameplate {
    border-color: #facc15;
    box-shadow:
      0 0 0 1px rgba(250, 204, 21, 0.55),
      inset 0 0 0 1px rgba(255, 220, 150, 0.15),
      0 4px 14px rgba(0, 0, 0, 0.4);
  }
  .enemy-focus-label {
    display: inline-block;
    margin-left: 0.4rem;
    padding: 0.08rem 0.3rem;
    border: 1px solid #facc15;
    border-radius: 0.2rem;
    background: #2b210d;
    color: #fde68a;
    font-size: 0.58em;
    font-weight: 800;
    letter-spacing: 0.08em;
    vertical-align: middle;
  }
  .enemy-card.targeted .enemy-sprite {
    filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.55)) drop-shadow(0 0 12px rgba(250, 204, 21, 0.45));
  }

  /* ===== C5 Combat FX pack (CSS/transform only) ===== */

  .enemy-card.fx-hit .enemy-sprite,
  .player-panel.fx-hit .player-bust img {
    animation: hitFlash 0.4s ease-out;
  }

  .enemy-card.fx-crit .enemy-sprite {
    animation: hitFlashCrit 0.45s ease-out;
  }

  .enemy-sprite-wrap.shake,
  .player-bust.shake {
    animation: hitShake 0.4s ease-out;
  }

  .enemy-card.fx-death .enemy-sprite {
    animation: deathDissolve 0.9s ease-out forwards;
  }

  .enemy-card.dead { opacity: 1; filter: none; }
  .enemy-card.dead .foe-plate { opacity: 0.65; }
  .enemy-card.dead .enemy-sprite {
    animation: none;
    opacity: 0.55;
    filter: grayscale(1) brightness(0.85);
  }
  .defeated-label {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.15rem 0.4rem;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.85);
    color: #cbd5e1;
    font: 700 0.65rem system-ui, sans-serif;
    z-index: 2;
  }

  .player-panel.fx-death .player-bust img {
    animation: deathDissolve 0.9s ease-out forwards;
  }

  .enemy-card.fx-cast .enemy-sprite,
  .player-panel.fx-cast .player-bust {
    animation: castGlow 0.7s ease-out;
  }

  .enemy-card.fx-miss .enemy-sprite {
    animation: missDim 0.45s ease-out;
  }

  @keyframes hitFlash {
    0% { filter: brightness(2.4) saturate(1.4) drop-shadow(0 0 10px #ef4444); }
    40% { filter: brightness(1.6) drop-shadow(0 0 6px #f87171); }
    100% { filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.55)); }
  }

  @keyframes hitFlashCrit {
    0% { filter: brightness(2.8) saturate(1.6) drop-shadow(0 0 14px #fbbf24); }
    50% { filter: brightness(1.8) drop-shadow(0 0 10px #f59e0b); }
    100% { filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.55)); }
  }

  @keyframes hitShake {
    0%, 100% { transform: translate3d(0, 0, 0); }
    15% { transform: translate3d(-7px, 1px, 0) rotate(-1.5deg); }
    30% { transform: translate3d(7px, -1px, 0) rotate(1.5deg); }
    45% { transform: translate3d(-5px, 0, 0); }
    60% { transform: translate3d(5px, 1px, 0); }
    75% { transform: translate3d(-2px, 0, 0); }
  }

  @keyframes deathDissolve {
    0% { opacity: 1; filter: brightness(1.8) drop-shadow(0 0 12px #f87171); transform: scale(1); }
    35% { opacity: 0.85; filter: brightness(1.2) grayscale(0.3); }
    100% {
      opacity: 0.2;
      filter: grayscale(1) brightness(0.55);
      transform: scale(0.92) translateY(6px);
    }
  }

  @keyframes castGlow {
    0% { box-shadow: 0 0 0 0 rgba(167, 139, 250, 0); filter: brightness(1); }
    30% {
      box-shadow: 0 0 22px 6px rgba(167, 139, 250, 0.55), 0 0 40px 2px rgba(232, 200, 120, 0.25);
      filter: brightness(1.35) saturate(1.25);
    }
    100% { box-shadow: 0 0 0 0 rgba(167, 139, 250, 0); filter: brightness(1); }
  }

  @keyframes missDim {
    0% { filter: brightness(1.15) drop-shadow(0 0 6px #94a3b8); opacity: 1; }
    40% { filter: brightness(0.85); opacity: 0.85; }
    100% { filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.55)); opacity: 1; }
  }

  .fx-float {
    position: absolute;
    left: 50%;
    top: 28%;
    transform: translateX(-50%);
    z-index: 4;
    pointer-events: none;
    animation: floatNum 0.85s ease-out forwards;
    font-family: system-ui, sans-serif;
    font-weight: 800;
    font-size: clamp(1.15rem, 2.6vw, 1.85rem);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.6);
    white-space: nowrap;
  }

  .fx-float.over-sprite {
    top: 18%;
    z-index: 6;
    font-size: clamp(2rem, 5.2vw, 3.4rem);
    font-weight: 900;
    letter-spacing: 0.02em;
    color: #fff;
    -webkit-text-stroke: 3px #140804;
    paint-order: stroke fill;
    text-shadow:
      0 2px 0 #140804,
      0 0 8px #000,
      0 4px 12px rgba(0, 0, 0, 0.9);
  }
  .fx-dmg { color: #fff; }
  .fx-dmg.crit { color: #fde68a; font-size: 1.28em; }
  .fx-dmg.crush { color: #fecaca; font-size: 1.45em; }
  .fx-heal { color: #bbf7d0; }
  .fx-miss-label {
    color: #fff;
    letter-spacing: 0.12em;
    font-size: 0.72em;
    font-weight: 900;
    text-transform: lowercase;
  }

  @keyframes floatNum {
    0% { opacity: 0; transform: translate(-50%, 8px) scale(0.8); }
    18% { opacity: 1; transform: translate(-50%, 0) scale(1.08); }
    70% { opacity: 1; transform: translate(-50%, -20px) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -28px) scale(1); }
  }

  .fx-puff {
    position: absolute;
    left: 50%;
    top: 42%;
    width: 28%;
    aspect-ratio: 1;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    pointer-events: none;
    z-index: 4;
    background: radial-gradient(circle, rgba(226, 232, 240, 0.85) 0%, rgba(148, 163, 184, 0.35) 45%, transparent 70%);
    box-shadow: 0 0 0 0 rgba(226, 232, 240, 0.4);
    animation: missPuff 0.55s ease-out forwards;
  }

  .fx-puff::after {
    content: '';
    position: absolute;
    inset: -35%;
    border-radius: 50%;
    border: 2px solid rgba(226, 232, 240, 0.45);
    animation: missPuffRing 0.55s ease-out forwards;
  }

  @keyframes missPuff {
    0% { opacity: 0; transform: translate(-50%, -50%) scale(0.35); }
    25% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -58%) scale(1.55); }
  }

  @keyframes missPuffRing {
    0% { opacity: 0.8; transform: scale(0.6); }
    100% { opacity: 0; transform: scale(1.4); }
  }

  .fx-slash {
    position: absolute;
    left: 18%;
    top: 22%;
    width: 64%;
    height: 10%;
    pointer-events: none;
    z-index: 4;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.95), #fca5a5, transparent);
    border-radius: 2px;
    transform: rotate(-28deg);
    box-shadow: 0 0 12px rgba(248, 113, 113, 0.7);
    animation: slashStreak 0.35s ease-out forwards;
  }

  .fx-slash.crit {
    background: linear-gradient(90deg, transparent, #fef3c7, #fbbf24, transparent);
    box-shadow: 0 0 16px rgba(251, 191, 36, 0.85);
    height: 12%;
  }

  @keyframes slashStreak {
    0% { opacity: 0; transform: rotate(-28deg) scaleX(0.2); }
    30% { opacity: 1; transform: rotate(-28deg) scaleX(1); }
    100% { opacity: 0; transform: rotate(-28deg) translateX(12%) scaleX(1.05); }
  }

  .fx-cast-glow,
  .fx-shield {
    position: absolute;
    inset: -6%;
    border-radius: inherit;
    pointer-events: none;
    z-index: 3;
  }

  .fx-cast-glow {
    background: radial-gradient(circle at 50% 40%, rgba(167, 139, 250, 0.45), transparent 65%);
    animation: castPulse 0.7s ease-out forwards;
  }

  .fx-shield {
    border: 2px solid rgba(96, 165, 250, 0.85);
    box-shadow: inset 0 0 18px rgba(59, 130, 246, 0.35), 0 0 16px rgba(59, 130, 246, 0.45);
    animation: shieldPulse 0.65s ease-out forwards;
  }

  @keyframes castPulse {
    0% { opacity: 0; transform: scale(0.85); }
    35% { opacity: 1; transform: scale(1.05); }
    100% { opacity: 0; transform: scale(1.15); }
  }

  @keyframes shieldPulse {
    0% { opacity: 0; transform: scale(0.9); }
    40% { opacity: 1; transform: scale(1.02); }
    100% { opacity: 0; transform: scale(1.08); }
  }

  .player-bust {
    position: relative;
  }

  .fx-layer {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    pointer-events: none;
    z-index: 2;
  }

  .fx-burst {
    animation: fxBurstPop 0.65s ease-out forwards;
    font-family: system-ui, sans-serif;
    font-weight: 800;
    font-size: clamp(1.1rem, 2.4vw, 1.7rem);
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
    letter-spacing: 0.06em;
  }

  .fx-burst.fx-cast {
    color: #ddd6fe;
    text-shadow: 0 0 16px rgba(167, 139, 250, 0.8), 0 2px 8px rgba(0, 0, 0, 0.85);
  }
  .fx-burst.fx-defend { color: #93c5fd; }
  .fx-burst.fx-flee { color: #fdba74; }

  .fx-burst-label { display: inline-block; }

  @keyframes fxBurstPop {
    0% { opacity: 0; transform: scale(0.75); }
    30% { opacity: 1; transform: scale(1.08); }
    100% { opacity: 0; transform: scale(1.2) translateY(-8px); }
  }

  /* Prefer GPU compositing for FX transforms */
  .enemy-sprite-wrap,
  .player-bust,
  .fx-float,
  .fx-puff,
  .fx-slash {
    will-change: transform, opacity;
  }

  .player-team {
    position: absolute;
    left: 1.1rem;
    right: 1.1rem;
    bottom: 0.85rem;
    z-index: 3;
    display: grid;
    grid-template-columns: minmax(320px, 36%) minmax(0, 1fr);
    grid-template-areas: "self allies";
    gap: 0.75rem;
    align-items: end;
  }
  .player-team.solo { grid-template-columns: minmax(320px, 440px); grid-template-areas: "self"; }
  .player-panel {
    grid-area: self;
    box-sizing: border-box;
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 0.9rem;
    min-width: 0;
    padding: 0.45rem 0.55rem 0.45rem 0.45rem;
    border: 1.5px solid rgba(212, 164, 74, 0.5);
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(14, 12, 10, 0.82), rgba(6, 6, 8, 0.7));
    box-shadow:
      0 10px 28px rgba(0, 0, 0, 0.45),
      inset 0 0 0 1px rgba(255, 220, 150, 0.08);
    backdrop-filter: blur(2px);
  }

  .join-banner {
    position: absolute;
    left: 50%;
    top: 44%;
    transform: translate(-50%, -50%);
    z-index: 8;
    padding: 0.45rem 1rem;
    border: 1px solid #d4a44a;
    border-radius: 6px;
    background: rgba(10, 9, 8, 0.94);
    color: #ffe1a0;
    font-weight: 800;
    white-space: nowrap;
    box-shadow: 0 4px 20px #000;
    pointer-events: none;
  }

  .ally-strip {
    grid-area: allies;
    box-sizing: border-box;
    z-index: 4;
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    min-width: 0;
    padding: 0.25rem;
  }
  .ally-card {
    display: flex;
    flex: 1 1 150px;
    max-width: 200px;
    box-sizing: border-box;
    min-width: 0;
    gap: 0.4rem;
    padding: 0.35rem;
    border: 1px solid rgba(212, 164, 74, 0.55);
    border-radius: 6px;
    background: rgba(8, 8, 10, 0.93);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  .ally-card.ally-turn { border-color: #fbbf24; box-shadow: 0 0 0 1px #fbbf24, 0 0 15px rgba(251, 191, 36, 0.5); }
  .ally-card.ally-down, .ally-card.ally-fled { opacity: 0.65; }
  .ally-card.ally-hit { animation: plateHit 0.32s ease-out; }
  .ally-card.ally-crit { animation: plateCrit 0.48s ease-out; }
  .ally-card.just-joined { animation: allyJoin 0.4s ease-out; }
  @keyframes allyJoin { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  .ally-portrait { position: relative; flex: 0 0 42px; height: 48px; }
  .ally-portrait img { width: 42px; height: 48px; object-fit: cover; object-position: top center; }
  .ally-portrait .fx-float.over-sprite { top: 0; font-size: 1.75rem; -webkit-text-stroke-width: 2px; }
  .ally-meta { min-width: 0; flex: 1; }
  .ally-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #f6e3b2; font-weight: 800; font-size: 0.8rem; }
  .ally-sub { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #aeb5c2; font-size: 0.62rem; text-transform: capitalize; }
  .ally-bar { height: 4px; margin-top: 0.22rem; border-radius: 3px; background: #251a18; overflow: hidden; }
  .ally-bar span { display: block; height: 100%; }
  .ally-bar.mana span { background: #60a5fa; }
  .ally-numbers { color: #cfd3dc; font-size: 0.59rem; line-height: 1.05; }

  .player-bust {
    position: relative;
    width: clamp(56px, 7.5vw, 92px);
    aspect-ratio: 1;
    border: 2px solid #d4a44a;
    border-radius: 5px;
    overflow: visible; /* C5: allow float numbers / puff outside bust */
    background: rgba(8, 8, 10, 0.95);
    box-shadow:
      0 0 0 3px rgba(8, 8, 10, 0.95),
      0 0 0 5px rgba(212, 164, 74, 0.55),
      0 8px 24px rgba(0, 0, 0, 0.5),
      inset 0 0 0 1px rgba(255, 220, 150, 0.2);
    flex-shrink: 0;
  }
  .foes-solo .player-bust {
    width: clamp(64px, 8.5vw, 108px);
  }
  .foes-swarm .player-bust {
    width: clamp(48px, 6vw, 72px);
  }

  .player-bust img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    border-radius: 3px;
    display: block;
  }

  .player-meta {
    min-width: 0;
    flex: 1;
    padding-right: 0.25rem;
  }

  .player-name {
    font-size: clamp(1.05rem, 2vw, 1.3rem);
    color: #e8c878;
    margin-bottom: 0.4rem;
    letter-spacing: 0.03em;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.75);
  }

  .player-hp,
  .player-mp {
    margin-bottom: 0.35rem;
    min-width: 0;
  }

  .player-panel .hp-track {
    height: 10px;
    border-radius: 3px;
    border-color: rgba(212, 164, 74, 0.45);
  }

  .status-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.4rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-family: system-ui, sans-serif;
    font-size: 0.7rem;
    padding: 0.18rem 0.5rem;
    border-radius: 4px;
    border: 1px solid rgba(212, 164, 74, 0.6);
    background: rgba(12, 10, 8, 0.82);
    color: #f5e6c0;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.35);
  }

  .chip i { font-size: 0.9rem; color: #d4a44a; }
  .focus-chip {
    border-color: rgba(168, 85, 247, 0.65);
    color: #e9d5ff;
  }
  .focus-chip i { color: #c084fc; }
  .wait-chip {
    border-color: rgba(212, 164, 74, 0.55);
    color: #f5e6c0;
    font-size: 0.82rem;
    padding: 0.28rem 0.7rem;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(28, 24, 16, 0.95), rgba(10, 10, 12, 0.95));
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35), 0 0 10px rgba(212, 164, 74, 0.12);
  }
  .wait-chip i { color: #e8c878; font-size: 1.05rem; }

  .battle-controls {
    position: relative;
    left: auto;
    bottom: auto;
    transform: none;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-end;
    gap: 0.3rem;
    width: auto;
    max-width: none;
    margin: 0.3rem 0.75rem 0.2rem;
    min-height: 0;
    z-index: 20;
    pointer-events: auto;
    box-sizing: border-box;
  }

  .dock-status {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    width: 100%;
    min-height: 0;
    pointer-events: none;
    z-index: 21;
  }
  .dock-status.has-chip {
    min-height: 40px;
  }

  .arena-hit-flash {
    position: absolute;
    inset: 0;
    z-index: 4;
    pointer-events: none;
    border-radius: inherit;
    background: radial-gradient(ellipse at 55% 40%, rgba(239, 68, 68, 0.28) 0%, rgba(239, 68, 68, 0.08) 42%, transparent 70%);
    animation: arenaFlashPulse 0.42s ease-out forwards;
  }
  .arena-hit-flash.crit {
    background: radial-gradient(ellipse at 55% 40%, rgba(251, 191, 36, 0.34) 0%, rgba(239, 68, 68, 0.12) 45%, transparent 72%);
    animation-duration: 0.52s;
  }
  .arena-hit-flash.heal {
    background: radial-gradient(ellipse at 40% 70%, rgba(34, 197, 94, 0.28) 0%, rgba(34, 197, 94, 0.08) 45%, transparent 72%);
  }
  @keyframes arenaFlashPulse {
    0% { opacity: 0; }
    18% { opacity: 1; }
    100% { opacity: 0; }
  }

  .action-banner-stack {
    position: absolute;
    left: 50%;
    top: 48%;
    transform: translate(-50%, -50%);
    z-index: 6;
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    gap: 0.35rem;
    width: min(94%, 640px);
    pointer-events: none;
  }

  .action-banner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    max-width: 100%;
    padding: 0.55rem 1.1rem;
    border-radius: 10px;
    border: 2px solid rgba(232, 200, 120, 0.78);
    background: linear-gradient(180deg, rgba(36, 28, 14, 0.96), rgba(10, 8, 6, 0.96));
    color: #f8fafc;
    font-family: system-ui, sans-serif;
    font-size: clamp(1.05rem, 2.1vw, 1.35rem);
    font-weight: 700;
    letter-spacing: 0.01em;
    text-align: center;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
    box-shadow:
      0 10px 28px rgba(0, 0, 0, 0.5),
      0 0 18px rgba(232, 200, 120, 0.18),
      inset 0 0 0 1px rgba(255, 220, 150, 0.12);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    animation: bannerPop 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.15);
    pointer-events: none;
  }
  .action-banner.flash {
    animation: bannerPop 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.15), bannerShake 0.42s ease-out;
  }
  .action-banner.kind-hit {
    border-color: rgba(248, 113, 113, 0.75);
    box-shadow:
      0 10px 28px rgba(0, 0, 0, 0.5),
      0 0 20px rgba(239, 68, 68, 0.22),
      inset 0 0 0 1px rgba(255, 180, 150, 0.1);
  }
  .action-banner.kind-crit {
    border-color: rgba(251, 191, 36, 0.9);
    box-shadow:
      0 10px 28px rgba(0, 0, 0, 0.5),
      0 0 24px rgba(251, 191, 36, 0.35),
      inset 0 0 0 1px rgba(255, 230, 150, 0.18);
  }
  .action-banner.kind-heal {
    border-color: rgba(74, 222, 128, 0.8);
    box-shadow:
      0 10px 28px rgba(0, 0, 0, 0.5),
      0 0 20px rgba(34, 197, 94, 0.25),
      inset 0 0 0 1px rgba(180, 255, 200, 0.12);
  }
  .action-banner.kind-miss {
    border-color: rgba(148, 163, 184, 0.7);
  }

  .banner-icon {
    flex: 0 0 auto;
    font-size: 1.45em !important;
    line-height: 1;
    color: #e8c878;
    filter: drop-shadow(0 0 6px rgba(232, 200, 120, 0.45));
  }
  .kind-hit .banner-icon { color: #f87171; filter: drop-shadow(0 0 6px rgba(239, 68, 68, 0.5)); }
  .kind-crit .banner-icon { color: #fbbf24; filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.65)); }
  .kind-heal .banner-icon { color: #4ade80; filter: drop-shadow(0 0 6px rgba(34, 197, 94, 0.5)); }
  .kind-miss .banner-icon { color: #cbd5e1; }

  .banner-copy {
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.28rem 0.4rem;
    min-width: 0;
    max-width: 100%;
  }
  .banner-actor { color: #f5e6c0; font-weight: 800; }
  .banner-verb {
    color: #e2e8f0;
    font-weight: 650;
    font-size: 0.92em;
    text-transform: lowercase;
  }
  .banner-target { color: #f8fafc; font-weight: 750; }
  .banner-for {
    color: #94a3b8;
    font-weight: 600;
    font-size: 0.88em;
  }
  .banner-amount {
    color: #fbbf24;
    font-weight: 900;
    font-variant-numeric: tabular-nums;
    font-size: 1.18em;
    text-shadow: 0 0 10px rgba(251, 191, 36, 0.45), 0 2px 6px rgba(0, 0, 0, 0.7);
  }
  .kind-hit .banner-amount,
  .kind-crit .banner-amount {
    color: #f87171;
    text-shadow: 0 0 10px rgba(239, 68, 68, 0.5), 0 2px 6px rgba(0, 0, 0, 0.7);
  }
  .kind-crit .banner-amount {
    color: #fde68a;
    text-shadow: 0 0 12px rgba(251, 191, 36, 0.65), 0 2px 6px rgba(0, 0, 0, 0.7);
  }
  .kind-heal .banner-amount {
    color: #86efac;
    text-shadow: 0 0 10px rgba(34, 197, 94, 0.5), 0 2px 6px rgba(0, 0, 0, 0.7);
  }
  .banner-raw { color: #f8fafc; }

  @keyframes bannerIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes bannerInArena {
    from { opacity: 0; transform: translateX(-50%) translateY(6px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
  @keyframes bannerPop {
    0% { opacity: 0; transform: translateY(10px) scale(0.88); }
    55% { opacity: 1; transform: translateY(-2px) scale(1.06); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes bannerShake {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    20% { transform: translateY(-1px) rotate(-0.8deg) scale(1.03); }
    40% { transform: translateY(1px) rotate(0.8deg) scale(1.04); }
    60% { transform: translateY(-1px) rotate(-0.5deg) scale(1.02); }
    80% { transform: translateY(0) rotate(0.35deg) scale(1.01); }
  }
  @keyframes spinSlow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin-slow {
    animation: spinSlow 2.4s linear infinite;
    display: inline-block;
  }

  .combat-hotbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    flex: 1 1 auto;
    min-width: 0;
  }

  .hb-slot {
    appearance: none;
    position: relative;
    flex: 0 0 52px;
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    box-sizing: border-box;
    border-radius: 7px;
    border: 1.5px dashed rgba(148, 163, 184, 0.28);
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    color: #94a3b8;
  }

  .hb-slot.filled {
    border-style: solid;
    border-color: rgba(148, 163, 184, 0.45);
    background: rgba(15, 23, 42, 0.8);
  }
  .hb-slot.skill.filled { border-color: rgba(167, 139, 250, 0.55); }
  .hb-slot.item.filled { border-color: rgba(96, 165, 250, 0.5); }
  .hb-slot.action.filled { border-color: rgba(212, 164, 74, 0.55); }
  .hb-slot.empty {
    opacity: 0.4;
    cursor: default;
  }
  .hb-slot.disabled:not(.empty) {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .hb-slot:hover:not(.disabled):not(.empty) {
    border-color: rgba(251, 191, 36, 0.7);
    background: rgba(30, 41, 59, 0.92);
  }
  .hb-slot img {
    width: 70%;
    height: 70%;
    object-fit: contain;
    image-rendering: pixelated;
    pointer-events: none;
  }
  .hb-index {
    position: absolute;
    left: 3px;
    top: 2px;
    font-size: 0.52rem;
    color: rgba(148, 163, 184, 0.7);
    line-height: 1;
    pointer-events: none;
    font-family: system-ui, sans-serif;
  }

  .queued-chip {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 1.05rem;
    border-radius: 999px;
    border: 2px solid rgba(167, 139, 250, 0.75);
    background: linear-gradient(180deg, rgba(42, 28, 64, 0.96), rgba(12, 10, 20, 0.96));
    color: #ede9fe;
    font-family: system-ui, sans-serif;
    font-size: clamp(0.95rem, 1.6vw, 1.12rem);
    font-weight: 800;
    letter-spacing: 0.02em;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4), 0 0 16px rgba(167, 139, 250, 0.28);
    animation: bannerIn 0.18s ease-out;
    pointer-events: none;
  }
  .queued-chip i { font-size: 1.35rem; color: #c4b5fd; }
  .queued-chip.wait,
  .queued-chip.status-pill.wait {
    border-color: rgba(232, 200, 120, 0.72);
    background: linear-gradient(180deg, rgba(36, 28, 14, 0.96), rgba(12, 10, 8, 0.96));
    color: #f5e6c0;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4), 0 0 16px rgba(232, 200, 120, 0.22);
  }
  .queued-chip.wait i { color: #e8c878; }
  .queued-name { letter-spacing: 0.03em; }
  .queued-cd {
    font-variant-numeric: tabular-nums;
    color: #f5d78c;
    padding: 0.12rem 0.5rem;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(232, 200, 120, 0.4);
    font-size: 0.85rem;
    font-weight: 800;
  }

  .dock-main {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: 0.65rem;
    width: 100%;
    box-sizing: border-box;
    max-width: none;
    margin: 0;
    padding: 0.4rem 0.7rem;
    border-radius: 10px;
    border: 1.5px solid rgba(212, 164, 74, 0.55);
    background: rgba(8, 8, 10, 0.94);
    box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.4),
      inset 0 0 0 1px rgba(255, 220, 150, 0.08),
      0 0 18px rgba(212, 164, 74, 0.12);
  }

  .dock-divider {
    flex: 0 0 1px;
    align-self: stretch;
    width: 1px;
    margin: 0.15rem 0;
    background: linear-gradient(180deg, transparent, rgba(212, 164, 74, 0.45), transparent);
  }

  /* Legacy aliases (unused) — keep harmless for any residual refs */
  .dock-row,
  .dock-strip {
    display: contents;
  }

  .hb-cd-overlay {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    border-radius: inherit;
    background: rgba(0, 0, 0, 0.62);
    color: #f8fafc;
    font-family: system-ui, sans-serif;
    font-weight: 800;
    font-size: 0.95rem;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
    pointer-events: none;
  }
  .hb-slot.on-cd img { filter: grayscale(0.7) brightness(0.7); }

  .battle-rail {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 0.28rem;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    flex: 0 0 auto;
    width: max-content;
    align-self: center;
    max-width: none;
  }
  .rail-btn {
    appearance: none;
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    box-sizing: border-box;
    border-radius: 7px;
    border: 1px solid rgba(148, 163, 184, 0.35);
    background: linear-gradient(180deg, rgba(18, 16, 14, 0.9), rgba(8, 7, 6, 0.9));
    color: #d1d5db;
    cursor: pointer;
    display: grid;
    place-items: center;
    padding: 0;
    box-shadow: none;
    opacity: 0.92;
    pointer-events: auto;
    flex: 0 0 auto;
  }
  .rail-label { display: none; }
  .rail-btn i { font-size: 1.25rem; color: #d4a44a; }
  .rail-btn:hover,
  .rail-btn.active {
    border-color: #e8c878;
    background: linear-gradient(180deg, rgba(36, 28, 14, 0.96), rgba(14, 12, 8, 0.96));
  }
  .rail-btn.primary {
    border: 1px solid rgba(212, 164, 74, 0.55);
    box-shadow: inset 0 0 0 1px rgba(255, 220, 150, 0.08);
  }
  .rail-btn.primary i { color: #e8c878; }
  .rail-btn.flee { border-color: rgba(239, 68, 68, 0.5); }
  .rail-btn.flee i { color: #f87171; }
  .rail-btn.scrap { border-color: rgba(56, 189, 248, 0.45); }
  .rail-btn.scrap i { color: #7dd3fc; }

  .battle-rail.essential {
    gap: 0;
  }

  .battle-dock {
    position: relative;
    left: auto;
    bottom: auto;
    transform: none;
    display: flex;
    gap: 0.55rem;
    padding: 0.2rem;
    z-index: 20;
  }



  .dock-btn {
    min-width: clamp(84px, 11vw, 108px);
    min-height: 74px;
    padding: 0.65rem 0.7rem 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    border-radius: 7px;
    border: 1.5px solid rgba(212, 164, 74, 0.7);
    background: linear-gradient(180deg, rgba(22, 18, 12, 0.94), rgba(8, 7, 6, 0.94));
    color: #f5e6c0;
    cursor: pointer;
    font-family: 'Cinzel', Georgia, serif;
    font-size: 0.78rem;
    letter-spacing: 0.05em;
    box-shadow:
      0 8px 18px rgba(0, 0, 0, 0.4),
      inset 0 0 0 1px rgba(255, 220, 150, 0.08);
  }

  .dock-btn i { font-size: 1.45rem; color: #d4a44a; }
  .dock-btn:hover,
  .dock-btn.active {
    border-color: #e8c878;
    box-shadow:
      0 0 0 1px rgba(232, 200, 120, 0.35),
      0 8px 18px rgba(0, 0, 0, 0.4),
      inset 0 0 0 1px rgba(255, 220, 150, 0.12);
    background: linear-gradient(180deg, rgba(36, 28, 14, 0.96), rgba(14, 12, 8, 0.96));
  }
  .dock-btn.flee { border-color: rgba(239, 68, 68, 0.5); }
  .dock-btn.flee i { color: #f87171; }

  .dock-panel {
    position: absolute;
    left: 50%;
    bottom: calc(100% + 0.35rem);
    transform: translateX(-50%);
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.4rem;
    max-width: min(92%, 520px);
    padding: 0.55rem;
    border: 1.5px solid rgba(212, 164, 74, 0.45);
    border-radius: 8px;
    background: rgba(8, 8, 10, 0.92);
    z-index: 30;
    pointer-events: auto;
  }

  .dock-panel-btn {
    appearance: none;
    border: 1px solid rgba(212, 164, 74, 0.4);
    background: rgba(20, 16, 10, 0.9);
    color: #f5e6c0;
    border-radius: 5px;
    padding: 0.4rem 0.65rem;
    font-family: system-ui, sans-serif;
    font-size: 0.8rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }
  .dock-panel-btn i { font-size: 1rem; color: #d4a44a; }
  .dock-panel-btn:hover { border-color: #e8c878; }
  .dock-empty {
    font-family: system-ui, sans-serif;
    font-size: 0.8rem;
    color: #9ca3af;
    padding: 0.35rem 0.6rem;
  }

  .combat-log {
    position: relative;
    left: auto;
    right: auto;
    bottom: auto;
    width: auto;
    height: 4.5rem;
    max-height: 4.5rem;
    min-height: 3.75rem;
    margin: 0.15rem 0.75rem 0.55rem;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0.4rem 0.75rem 0.45rem;
    border: 1.5px solid rgba(212, 164, 74, 0.55);
    border-radius: 6px;
    background: linear-gradient(180deg, rgba(12, 10, 8, 0.96), rgba(4, 4, 6, 0.96));
    box-shadow:
      inset 0 0 0 1px rgba(255, 220, 150, 0.06),
      0 4px 14px rgba(0, 0, 0, 0.35);
    font-family: system-ui, sans-serif;
    font-size: 0.82rem;
    line-height: 1.35;
    color: #d1d5db;
    box-sizing: border-box;
    z-index: 2;
  }

  .combat-log-title {
    appearance: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.35rem;
    width: 100%;
    margin: 0 0 0.25rem;
    padding: 0;
    border: none;
    background: transparent;
    color: #d4a44a;
    font-family: inherit;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-align: left;
    cursor: pointer;
    touch-action: manipulation;
  }
  .log-expand-icon {
    display: inline-flex;
    font-size: 1rem;
    color: rgba(212, 164, 74, 0.75);
  }
  .combat-log.log-expanded {
    height: 10.5rem;
    max-height: 10.5rem;
    min-height: 8rem;
  }
  .combat-log-body {
    min-height: 0;
  }

  .combat-log-line {
    white-space: normal;
    overflow: hidden;
    word-break: break-word;
  }
  .combat-log-line.muted {
    color: #9ca3af;
    opacity: 0.85;
  }
  .combat-event-card {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin: 0.15rem 0;
    padding: 0.12rem 0.45rem;
    border: 1px solid rgba(212, 164, 74, 0.75);
    border-radius: 999px;
    background: rgba(40, 28, 8, 0.72);
    color: #e8c878;
    font-size: 0.72rem;
    line-height: 1.25;
  }
  .combat-event-card .material-icons {
    font-size: 0.95rem;
    color: #d4a44a;
    flex: 0 0 auto;
  }
  .combat-event-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .combat-event-source {
    margin-left: auto;
    flex: 0 0 auto;
    font-size: 0.62rem;
    letter-spacing: 0.04em;
    color: rgba(232, 200, 120, 0.85);
    white-space: nowrap;
  }

  .outcome-panel {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    gap: 0.6rem;
    background: rgba(0, 0, 0, 0.62);
    z-index: 5;
    text-align: center;
    padding: 1.5rem;
    animation: stageIn 0.25s ease-out;
  }

  .outcome-card {
    min-width: min(92%, 360px);
    max-width: 28rem;
    padding: 1.4rem 1.6rem 1.25rem;
    border-radius: 12px;
    border: 2px solid rgba(212, 164, 74, 0.75);
    background:
      radial-gradient(ellipse at 50% 0%, rgba(80, 55, 18, 0.55), transparent 60%),
      linear-gradient(180deg, rgba(28, 20, 10, 0.97), rgba(8, 6, 4, 0.97));
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.55),
      0 0 0 4px rgba(212, 164, 74, 0.22),
      0 18px 48px rgba(0, 0, 0, 0.55),
      inset 0 0 0 1px rgba(255, 220, 150, 0.12);
  }

  .outcome-ornament {
    color: #d4a44a;
    font-size: 0.85rem;
    letter-spacing: 0.4em;
    margin-bottom: 0.35rem;
    opacity: 0.85;
  }

  .outcome-title {
    font-size: clamp(1.8rem, 5vw, 2.6rem);
    letter-spacing: 0.14em;
    color: #e8c878;
    text-transform: uppercase;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.65);
    margin-bottom: 0.55rem;
  }

  .outcome-panel.victory .outcome-title { color: #fbbf24; }
  .outcome-panel.defeat .outcome-title { color: #f87171; }
  .outcome-panel.fled .outcome-title { color: #93c5fd; }

  .outcome-summary {
    font-family: system-ui, sans-serif;
    color: #e5e7eb;
    font-size: 0.9rem;
    line-height: 1.4;
    margin: 0 auto 0.85rem;
    max-width: 24rem;
    opacity: 0.92;
  }

  .outcome-msg {
    font-family: system-ui, sans-serif;
    max-width: 28rem;
    color: #e5e7eb;
    font-size: 0.95rem;
  }

  .reward-breakdown {
    list-style: none;
    margin: 0.35rem 0 0.6rem;
    padding: 0;
    text-align: left;
    font-family: system-ui, sans-serif;
    font-size: 0.78rem;
    line-height: 1.45;
    color: #e5e7eb;
  }
  .reward-breakdown li {
    padding: 0.12rem 0;
  }

  .outcome-rewards {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.45rem;
    margin-bottom: 1rem;
  }
  .reward-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-family: system-ui, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    border: 1px solid rgba(212, 164, 74, 0.55);
    background: rgba(12, 10, 8, 0.85);
    color: #f5e6c0;
  }
  .reward-chip i { font-size: 1rem; }
  .reward-chip.xp i { color: #fbbf24; }
  .reward-chip.gold i { color: #e8c878; }

  .outcome-continue {
    appearance: none;
    min-width: 10rem;
    padding: 0.7rem 1.4rem;
    border-radius: 8px;
    border: 2px solid #e8c878;
    background: linear-gradient(180deg, rgba(48, 34, 14, 0.98), rgba(22, 16, 8, 0.98));
    color: #f5e6c0;
    font-family: 'Cinzel', Georgia, serif;
    font-size: 0.95rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow:
      0 0 0 3px rgba(8, 7, 6, 0.95),
      0 0 0 5px rgba(232, 200, 120, 0.55),
      0 8px 18px rgba(0, 0, 0, 0.45);
  }
  .outcome-continue:hover {
    border-color: #f5d78c;
    box-shadow:
      0 0 0 3px rgba(8, 7, 6, 0.95),
      0 0 0 5px rgba(245, 215, 140, 0.8),
      0 0 22px rgba(232, 200, 120, 0.35);
  }

  .battle-stage.ending {
    pointer-events: none;
    background: none;
  }
  .battle-stage.ending .battle-backdrop {
    display: none;
  }
  .battle-stage.ending .battle-frame {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    pointer-events: none;
  }
  .battle-stage.ending .battle-header,
  .battle-stage.ending .decision-timer,
  .battle-stage.ending .battle-arena,
  .battle-stage.ending .battle-controls,
  .battle-stage.ending .combat-log {
    visibility: hidden;
  }
  .battle-stage.ending .outcome-panel {
    pointer-events: none;
    background: transparent;
    place-content: start;
    justify-items: start;
    padding: 4.75rem 1.25rem 1rem;
  }
  .battle-stage.ending .outcome-card {
    pointer-events: auto;
    cursor: pointer;
    max-width: min(26rem, 38vw);
    text-align: left;
  }
  .battle-stage.ending .outcome-title,
  .battle-stage.ending .outcome-ornament,
  .battle-stage.ending .outcome-rewards,
  .battle-stage.ending .outcome-continue {
    text-align: center;
  }
  .battle-stage.ending .outcome-rewards,
  .battle-stage.ending .outcome-continue {
    display: flex;
  }
  .battle-stage.ending .outcome-continue {
    margin-left: auto;
    margin-right: auto;
  }

  .loot-reveal {
    list-style: none;
    margin: 0.2rem 0 0.7rem;
    padding: 0;
    font-family: system-ui, sans-serif;
    font-size: 0.92rem;
    font-weight: 700;
  }
  .loot-item {
    padding: 0.18rem 0;
    animation: lootIn 0.28s ease-out;
  }
  /* Rarity-unique drop: a real loot row, first in the list, gold with a quiet tag. */
  .loot-item.loot-unique {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin: 0.05rem 0 0.35rem;
    padding: 0.4rem 0.65rem 0.4rem 0.55rem;
    border-left: 3px solid #e8c25a;
    border-radius: 3px;
    background: linear-gradient(90deg, rgba(232, 194, 90, 0.17), rgba(232, 194, 90, 0.04) 70%, rgba(232, 194, 90, 0));
  }
  .loot-unique-gem {
    font-size: 1.15rem;
    color: #f3d27a;
    filter: drop-shadow(0 0 5px rgba(243, 210, 122, 0.55));
    flex: 0 0 auto;
  }
  .loot-unique-body {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    line-height: 1.2;
  }
  .unique-tag {
    font-family: var(--font-display, 'Cinzel', Georgia, serif);
    font-size: 0.6rem;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(232, 200, 120, 0.78);
  }
  .loot-unique-name {
    font-size: 1rem;
    font-weight: 700;
    color: #f6d77e;
    text-shadow: 0 0 10px rgba(246, 215, 126, 0.35);
  }
  .combat-unique-line {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0.2rem 0;
    padding: 0.2rem 0.5rem 0.2rem 0.45rem;
    border-left: 3px solid #e8c25a;
    border-radius: 3px;
    background: linear-gradient(90deg, rgba(232, 194, 90, 0.16), rgba(232, 194, 90, 0));
    line-height: 1.3;
  }
  .combat-unique-line .material-icons {
    font-size: 0.95rem;
    color: #f3d27a;
    filter: drop-shadow(0 0 4px rgba(243, 210, 122, 0.5));
    flex: 0 0 auto;
  }
  .combat-unique-line .unique-tag {
    font-size: 0.56rem;
    flex: 0 0 auto;
  }
  .combat-unique-name {
    min-width: 0;
    font-weight: 700;
    color: #f6d77e;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .rarity-normal { color: #e5e7eb; }
  .rarity-magic { color: #60a5fa; }
  .rarity-rare { color: #facc15; }
  .rarity-legendary { color: #fb923c; }
  .rarity-mythic { color: #e879f9; }
  @keyframes lootIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
  }

  .level-callout {
    margin: 0.35rem 0 0.8rem;
    padding: 0.45rem 0.7rem;
    border: 1px solid rgba(251, 191, 36, 0.85);
    border-radius: 8px;
    background: rgba(48, 34, 8, 0.92);
    color: #fde68a;
    font-size: 1.15rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    text-align: center;
  }

    .battle-stage.reduced-motion .phase-banner { animation: none; }
    .battle-stage.reduced-motion .ally-card.just-joined,
    .battle-stage.reduced-motion .ally-card.ally-hit,
    .battle-stage.reduced-motion .ally-card.ally-crit { animation: none; }
    .battle-stage.reduced-motion .fx-float,
    .battle-stage.reduced-motion .fx-slash,
    .battle-stage.reduced-motion .fx-puff,
    .battle-stage.reduced-motion .nameplate.hit-flash,
    .battle-stage.reduced-motion .nameplate.crit-flash,
    .battle-stage.reduced-motion .nameplate.crush-flash,
    .battle-stage.reduced-motion .player-name.hit-flash,
    .battle-stage.reduced-motion .player-name.crit-flash,
    .battle-stage.reduced-motion .player-name.crush-flash,
    .battle-stage.reduced-motion .enemy-card.fx-hit .enemy-sprite,
    .battle-stage.reduced-motion .enemy-card.fx-crit .enemy-sprite,
    .battle-stage.reduced-motion .enemy-sprite-wrap.shake,
    .battle-stage.reduced-motion .loot-item {
      animation: none !important;
    }
    .battle-stage.reduced-motion .fx-float { opacity: 1; }

  /* Primary Attack — double gold border glow (C0 mock) */
  .dock-btn.primary {
    border: 2px solid #e8c878;
    background: linear-gradient(180deg, rgba(48, 34, 14, 0.98), rgba(22, 16, 8, 0.98));
    box-shadow:
      0 0 0 3px rgba(8, 7, 6, 0.95),
      0 0 0 5px rgba(232, 200, 120, 0.75),
      0 0 22px rgba(232, 200, 120, 0.35),
      0 8px 18px rgba(0, 0, 0, 0.45),
      inset 0 0 0 1px rgba(255, 230, 170, 0.18);
  }
  .dock-btn.primary i { color: #f5d78c; text-shadow: 0 0 10px rgba(232, 200, 120, 0.45); }
  .dock-btn.primary:hover,
  .dock-btn.primary.active {
    border-color: #f5d78c;
    box-shadow:
      0 0 0 3px rgba(8, 7, 6, 0.95),
      0 0 0 5px rgba(245, 215, 140, 0.9),
      0 0 28px rgba(232, 200, 120, 0.45),
      0 8px 18px rgba(0, 0, 0, 0.45),
      inset 0 0 0 1px rgba(255, 230, 170, 0.22);
  }

  .decision-timer-label {
    display: block;
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    font-family: system-ui, sans-serif;
    font-size: 0.68rem;
    font-weight: 700;
    color: #f8fafc;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
    letter-spacing: 0.02em;
    pointer-events: none;
    z-index: 1;
    white-space: nowrap;
  }
  .decision-timer {
    height: 10px;
    border-radius: 4px;
  }

  .dock-sheet-backdrop {
    display: none;
  }
  .dock-sheet-head {
    display: none;
  }
  .dock-sheet-tabs,
  .dock-tab,
  .dock-sheet-close {
    display: none;
  }

  /* Wide desktop: gutters beside combat chrome (viewport > 1280px) */
  @media (min-width: 1281px) {
    .battle-stage {
      padding: 2rem 2.5rem;
    }
    .battle-frame {
      width: min(1280px, calc(100vw - 5rem));
      height: min(calc(100vh - 4rem), 960px);
      max-width: min(1280px, calc(100vw - 5rem));
      max-height: min(calc(100vh - 4rem), 960px);
    }
  }

  /* C7-mobile: thumb-first stack ≤768px — header / arena / dock / log-peek */
  @media (max-width: 768px) {
    .battle-stage {
      display: flex;
      align-items: stretch;
      justify-content: stretch;
      padding: 0;
      height: 100dvh;
      max-height: 100dvh;
    }

    .battle-frame {
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      max-width: none;
      max-height: none;
      border-radius: 0;
      border: none;
      display: grid;
      grid-template-rows:
        auto
        auto
        minmax(0, 1fr)
        auto
        auto;
      grid-template-areas:
        "header"
        "timer"
        "arena"
        "dock"
        "log";
      padding-bottom: env(safe-area-inset-bottom, 0px);
      overflow: hidden;
    }

    .battle-arena {
      grid-area: arena;
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: minmax(0, 1fr) auto auto;
      grid-template-areas:
        "enemies"
        "fx"
        "player";
      min-height: 0;
      overflow: hidden;
      position: relative;
    }

    .battle-header {
      grid-area: header;
      padding: 0.45rem 0.65rem 0.15rem;
      gap: 0.35rem;
      flex-wrap: wrap;
    }
    .header-label { font-size: 0.85rem; }
    .header-icon { font-size: 1.05rem; }
    .round-chip,
    .turn-chip {
      font-size: 0.62rem;
      padding: 0.1rem 0.4rem;
    }
    .header-rule { display: none; }

    .decision-timer {
      grid-area: timer;
      margin: 0.15rem 0.65rem 0.1rem;
      height: 8px;
      border-radius: 4px;
      border-width: 1.5px;
    }
    .decision-timer.idle {
      height: 0;
      margin: 0;
      border: none;
    }
    .decision-timer-label {
      display: block;
      position: absolute;
      right: 0.4rem;
      top: 50%;
      transform: translateY(-50%);
      font-family: system-ui, sans-serif;
      font-size: 0.65rem;
      font-weight: 700;
      color: #f8fafc;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
      letter-spacing: 0.02em;
      pointer-events: none;
      z-index: 1;
    }

    .enemy-strip {
      grid-area: enemies;
      position: relative;
      top: auto;
      right: auto;
      justify-self: stretch;
      align-self: stretch;
      justify-content: center;
      align-content: center;
      max-width: none;
      width: 100%;
      padding: 0.2rem 0.5rem 0;
      gap: 0.45rem;
      overflow: hidden;
    }
    .enemy-card {
      width: clamp(73px, 27vw, 112px);
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    }
    .enemy-strip.pack-solo .enemy-card {
      width: clamp(120px, 43vw, 213px);
    }
    .enemy-strip.pack-duo .enemy-card {
      width: clamp(80px, 25vw, 120px);
    }
    .enemy-strip.pack-swarm .enemy-card {
      width: clamp(57px, 17vw, 88px);
    }
    .enemy-card:active:not(:disabled) {
      transform: scale(0.97);
    }
    .enemy-sprite-wrap {
      aspect-ratio: 1;
      max-height: min(21vh, 147px);
    }
    .enemy-strip.pack-solo .enemy-sprite-wrap {
      max-height: min(31vh, 227px);
    }
    .nameplate { font-size: 0.74rem; }
    .hp-track { height: 9px; }
    .hp-nums { font-size: 0.68rem; }

    .fx-layer {
      grid-area: fx;
      position: relative;
      inset: auto;
      min-height: 28px;
      z-index: 2;
    }

    .action-banner-stack {
      top: 35%;
      position: absolute;
      left: 50%;
      bottom: auto;
      transform: translate(-50%, -50%);
      margin: 0;
      width: calc(100% - 1rem);
      max-width: calc(100% - 1rem);
      z-index: 6;
    }
    .action-banner {
      position: relative;
      left: auto;
      bottom: auto;
      transform: none;
      margin: 0;
      max-width: 100%;
      font-size: clamp(0.95rem, 3.6vw, 1.12rem);
      padding: 0.42rem 0.75rem;
      width: 100%;
      box-sizing: border-box;
      white-space: normal;
    }
    .banner-icon { font-size: 1.25em !important; }
    .banner-amount { font-size: 1.12em; }

    /* Compact horizontal Self strip — free vertical space for arena */
    .player-team, .player-team.solo {
      grid-area: player;
      position: relative;
      left: auto;
      right: auto;
      bottom: auto;
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas: "self" "allies";
      gap: 0;
      min-width: 0;
    }
    .player-panel {
      max-width: none;
      width: calc(100% - 1rem);
      margin: 0.1rem 0.5rem 0.2rem;
      padding: 0.28rem 0.45rem;
      border: 1px solid rgba(212, 164, 74, 0.5);
      border-radius: 7px;
      background: rgba(8, 8, 10, 0.9);
      box-shadow: 0 3px 12px rgba(0, 0, 0, 0.3);
      backdrop-filter: none;
      gap: 0.5rem;
      align-items: center;
      z-index: 3;
    }
    .ally-strip {
      grid-area: allies;
      flex-wrap: nowrap;
      max-width: none;
      width: calc(100% - 1rem);
      margin: 0.1rem 0.5rem;
      padding: 1.4rem 0.1rem 0.1rem;
      margin-top: -1.2rem;
      overflow-x: auto;
    }
    .ally-card { flex: 0 0 138px; padding: 0.2rem; }
    .ally-portrait { flex-basis: 32px; height: 42px; }
    .ally-portrait img { width: 32px; height: 42px; }
    .join-banner { top: 35%; max-width: 90%; font-size: 0.85rem; }
    .player-bust {
      width: clamp(40px, 11vw, 48px);
      border-radius: 6px;
      box-shadow:
        0 0 0 2px rgba(8, 8, 10, 0.95),
        0 0 0 3px rgba(212, 164, 74, 0.45),
        0 3px 10px rgba(0, 0, 0, 0.35);
    }
    .player-meta {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem 0.55rem;
      padding-right: 0;
    }
    .player-name {
      font-size: 0.88rem;
      margin-bottom: 0;
      flex: 0 1 auto;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 42%;
    }
    .player-hp,
    .player-mp {
      min-width: 0;
      flex: 1 1 5.5rem;
      margin-bottom: 0;
      max-width: 9rem;
    }
    .player-panel .hp-track { height: 8px; }
    .player-panel .hp-label { font-size: 0.6rem; }
    .player-panel .hp-nums { font-size: 0.6rem; }
    .status-chips { display: none; }

    /* Dock: queue chip → full-width hotbar → utility row (never beside log) */
    .battle-controls {
      grid-area: dock;
      box-sizing: border-box;
      min-width: 0;
      position: relative;
      left: auto;
      bottom: auto;
      transform: none;
      width: calc(100% - 0.6rem);
      max-width: none;
      margin: 0.1rem 0.3rem 0.15rem;
      gap: 0.25rem;
      min-height: 0;
      z-index: 20;
      align-items: stretch;
    }
    .dock-status {
      align-items: stretch;
    }
    .dock-status.has-chip {
      min-height: 36px;
    }
    .queued-chip {
      align-self: center;
      font-size: 0.92rem;
      padding: 0.38rem 0.9rem;
    }
    .queued-chip i { font-size: 1.2rem; }
    .dock-main {
      display: flex;
      box-sizing: border-box;
      min-width: 0;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 0.35rem;
      width: 100%;
      max-width: none;
      margin: 0;
      padding: 0.35rem;
    }
    .dock-divider {
      order: 2;
      width: 100%;
      height: 1px;
      margin: 0;
      align-self: stretch;
      background: linear-gradient(90deg, transparent, rgba(212, 164, 74, 0.4), transparent);
    }
    .combat-hotbar {
      order: 1;
      min-width: 0;
      width: 100%;
      flex: 0 0 auto;
      box-sizing: border-box;
      gap: 0.28rem;
      padding: 0;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      justify-content: space-between;
    }
    .hb-slot {
      width: clamp(44px, 11.5vw, 52px);
      height: clamp(44px, 11.5vw, 52px);
      min-width: 44px;
      min-height: 44px;
      flex: 1 1 0;
      max-width: 52px;
    }
    .battle-rail {
      order: 3;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: stretch;
      justify-content: stretch;
      width: 100%;
      max-width: none;
      align-self: stretch;
      gap: 0.3rem;
      padding: 0.2rem;
      box-sizing: border-box;
    }
    .battle-rail.essential {
      justify-content: flex-end;
      width: auto;
      max-width: 100%;
      align-self: flex-end;
    }
    .battle-rail.essential .rail-btn.flee {
      flex: 0 0 auto;
      min-width: 4.5rem;
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
    .rail-btn {
      flex: 1 1 0;
      width: auto;
      height: auto;
      min-width: 0;
      min-height: 48px;
      padding: 0.35rem 0.15rem 0.3rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.1rem;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    }
    .rail-btn i { font-size: 1.3rem; }
    .rail-label {
      display: block;
      font-family: system-ui, sans-serif;
      font-size: 0.62rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      color: #e5e7eb;
      line-height: 1.1;
    }
    .rail-btn.primary .rail-label { color: #f5e6c0; }
    .rail-btn.flee .rail-label { color: #fca5a5; }
    .rail-btn:active {
      transform: scale(0.97);
    }

    .battle-dock {
      position: relative;
      left: auto;
      bottom: auto;
      transform: none;
      width: 100%;
      max-width: none;
      margin: 0;
      padding: 0.35rem;
      gap: 0.3rem;
      justify-content: stretch;
      border: 1.5px solid rgba(212, 164, 74, 0.5);
      border-radius: 10px;
      background: rgba(8, 8, 10, 0.92);
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.35);
      z-index: 3;
      box-sizing: border-box;
    }
    .dock-btn {
      min-width: 0;
      flex: 1;
      min-height: 56px;
      padding: 0.55rem 0.2rem 0.4rem;
      font-size: 0.68rem;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    }
    .dock-btn i { font-size: 1.35rem; }
    .dock-btn.primary {
      flex: 1.55;
      min-height: 64px;
      font-size: 0.78rem;
      font-weight: 700;
    }
    .dock-btn.primary i { font-size: 1.55rem; }
    .dock-btn.secondary {
      opacity: 0.95;
    }
    .dock-btn:active {
      transform: scale(0.97);
    }

    .dock-sheet-backdrop {
      display: block;
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.45);
      z-index: 4;
    }
    .dock-panel.sheet {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      transform: none;
      max-width: none;
      width: 100%;
      max-height: min(52vh, 420px);
      padding: 0.55rem 0.65rem calc(0.65rem + env(safe-area-inset-bottom, 0px));
      border-radius: 14px 14px 0 0;
      border: 1.5px solid rgba(212, 164, 74, 0.5);
      border-bottom: none;
      background: rgba(8, 8, 10, 0.97);
      z-index: 5;
      flex-direction: column;
      flex-wrap: nowrap;
      align-items: stretch;
      gap: 0.35rem;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      animation: sheetUp 0.22s ease-out;
    }
    @keyframes sheetUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
    .dock-sheet-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin-bottom: 0.25rem;
      padding-bottom: 0.35rem;
      border-bottom: 1px solid rgba(212, 164, 74, 0.25);
    }
    .dock-sheet-tabs {
      display: flex;
      gap: 0.35rem;
    }
    .dock-tab {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      appearance: none;
      border: 1px solid rgba(212, 164, 74, 0.35);
      background: rgba(20, 16, 10, 0.85);
      color: #c4b5a0;
      border-radius: 999px;
      padding: 0.35rem 0.7rem;
      font-family: system-ui, sans-serif;
      font-size: 0.75rem;
      cursor: pointer;
      touch-action: manipulation;
    }
    .dock-tab i { font-size: 0.95rem; color: #d4a44a; }
    .dock-tab.active {
      border-color: #e8c878;
      color: #f5e6c0;
      background: rgba(40, 28, 12, 0.95);
    }
    .dock-sheet-close {
      display: inline-flex;
      appearance: none;
      border: 1px solid rgba(255, 255, 255, 0.15);
      background: rgba(255, 255, 255, 0.06);
      color: #9ca3af;
      border-radius: 8px;
      padding: 0.35rem;
      cursor: pointer;
      touch-action: manipulation;
    }
    .dock-sheet-close i { font-size: 1.15rem; }
    .dock-panel-btn {
      min-height: 48px;
      justify-content: flex-start;
      font-size: 0.9rem;
      padding: 0.55rem 0.75rem;
      touch-action: manipulation;
    }

    /* Log peek (~3.5–4.5rem) → expand ~40% of frame; never under absolute dock */
    .combat-log {
      grid-area: log;
      position: relative;
      left: auto;
      right: auto;
      bottom: auto;
      width: calc(100% - 1rem);
      margin: 0 0.5rem 0.3rem;
      height: 4rem;
      min-height: 3.5rem;
      max-height: 4.5rem;
      font-size: 0.7rem;
      padding: 0.3rem 0.55rem 0.35rem;
      transition: height 0.2s ease, max-height 0.2s ease, min-height 0.2s ease;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      z-index: 2;
    }
    .combat-log.log-peek {
      height: 4rem;
      min-height: 3.5rem;
      max-height: 4.5rem;
    }
    .combat-log.log-expanded {
      height: 40%;
      min-height: 12rem;
      max-height: 45%;
    }
    .combat-log-title {
      margin-bottom: 0.15rem;
      padding: 0.1rem 0;
      min-height: 1.25rem;
    }
    .log-expand-icon {
      display: inline-flex;
    }
    .combat-log-body {
      overflow: visible;
    }

    .arena-art {
      filter: brightness(0.38) saturate(0.7);
    }

    .outcome-panel {
      padding: 1rem;
    }
  }


  .buff-row {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    margin: 0;
    max-width: 100%;
  }
  /* WoW-style: small rectangles above/beside the combat portrait */
  .buff-row.portrait-buffs {
    position: absolute;
    z-index: 5;
    left: 0;
    top: 0;
    transform: translateY(calc(-100% - 3px));
    margin: 0;
    max-width: 140px;
    pointer-events: auto;
  }
  .ally-portrait .buff-row.portrait-buffs {
    left: calc(100% + 3px);
    top: 0;
    transform: none;
    flex-direction: column;
    max-width: 28px;
  }
  .enemy-sprite-wrap .buff-row.portrait-buffs {
    left: 50%;
    top: 0;
    transform: translate(-50%, calc(-100% - 2px));
    justify-content: center;
    max-width: 120px;
  }
  .buff-icon {
    position: relative;
    width: 20px;
    height: 24px;
    border-radius: 3px;
    border: 1px solid rgba(212, 175, 55, 0.65);
    background: rgba(8, 10, 16, 0.94);
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
    flex: 0 0 auto;
  }
  .buff-icon.debuff, .buff-icon.dot, .buff-icon.stun { border-color: rgba(248, 113, 113, 0.75); }
  .buff-icon.hot { border-color: rgba(74, 222, 128, 0.7); }
  .buff-icon.buff { border-color: rgba(96, 165, 250, 0.7); }
  .buff-icon img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    image-rendering: pixelated;
    display: block;
  }
  .buff-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 10px;
    font-weight: 700;
    color: #fde68a;
    text-transform: uppercase;
  }
  .buff-stacks {
    position: absolute;
    right: 0;
    bottom: 0;
    min-width: 11px;
    padding: 0 2px;
    font-size: 9px;
    line-height: 11px;
    font-weight: 800;
    color: #fff;
    background: rgba(0,0,0,0.78);
    border-top-left-radius: 3px;
    text-align: center;
  }

  /* ===== Layout B PoC — room sprites + edge info frames ===== */
  .battle-stage.layout-b .layout-b-chip {
    border-color: rgba(250, 204, 21, 0.85);
    color: #fde68a;
    background: rgba(40, 28, 8, 0.9);
  }

  /* Edge-docked detail frames (TL player / TR target) */
  .battle-stage.layout-b .layout-b-frame {
    position: absolute;
    z-index: 6;
    top: 0.4rem;
    width: min(248px, 24%);
    box-sizing: border-box;
    padding: 0.32rem 0.42rem 0.36rem;
    border: 1px solid rgba(212, 164, 74, 0.5);
    border-radius: 7px;
    background: rgba(10, 8, 6, 0.9);
    background-image: linear-gradient(160deg, rgba(18, 14, 10, 0.94), rgba(6, 6, 8, 0.88));
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
    pointer-events: none;
  }
  .battle-stage.layout-b .layout-b-frame.player-frame {
    left: 0.45rem;
    right: auto;
  }
  .battle-stage.layout-b .layout-b-frame.target-frame {
    right: 0.45rem;
    left: auto;
    text-align: left;
  }
  .battle-stage.layout-b .lb-name {
    font-family: 'Cinzel', Georgia, serif;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: none;
    color: #f5e6c0;
    margin-bottom: 0.18rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .battle-stage.layout-b .target-frame .lb-name {
    color: #fde68a;
    text-transform: none;
    font-size: 0.8rem;
    letter-spacing: 0.03em;
  }
  .battle-stage.layout-b .lb-hp,
  .battle-stage.layout-b .lb-mp {
    margin-bottom: 0.14rem;
  }
  .battle-stage.layout-b .layout-b-frame .hp-track {
    height: 7px;
  }
  .battle-stage.layout-b .layout-b-frame .hp-label,
  .battle-stage.layout-b .layout-b-frame .hp-nums {
    font-size: 0.58rem;
  }
  .battle-stage.layout-b .lb-buffs {
    margin-top: 0.18rem;
    max-width: 100%;
    gap: 0.18rem;
  }
  .battle-stage.layout-b .lb-buffs .buff-icon {
    width: 22px;
    height: 22px;
  }
  .battle-stage.layout-b .lb-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.18rem;
    margin-top: 0.22rem;
  }
  .battle-stage.layout-b .lb-chips .chip {
    font-size: 0.58rem;
    padding: 0.08rem 0.32rem;
  }
  .battle-stage.layout-b .lb-chips .wait-chip {
    opacity: 0.85;
    font-weight: 600;
  }
  /* Mid-fight chrome stays quiet: no Resolving/Waiting pill, no turn-name chip.
     The round chip and the your-turn countdown remain. */
  .battle-stage.layout-b .decision-timer.waiting,
  .battle-stage.layout-b .decision-timer.idle {
    display: none;
  }
  .battle-stage.layout-b .dock-status .queued-chip.wait {
    display: none;
  }
  .battle-stage.layout-b .dock-status,
  .battle-stage.layout-b .dock-status.has-chip {
    min-height: 0;
  }
  .battle-stage.layout-b .dock-status .queued-chip:not(.wait) {
    padding: 0.28rem 0.72rem;
    font-size: 0.78rem;
    border-width: 1.5px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
  }

  /* Enemy field RIGHT — sprites on markers; tiny HP only over sprite */
  .battle-stage.layout-b .enemy-strip,
  .battle-stage.layout-b .enemy-strip.pack-solo,
  .battle-stage.layout-b .enemy-strip.pack-duo,
  .battle-stage.layout-b .enemy-strip.pack-swarm {
    top: auto;
    bottom: 14%;
    right: 7%;
    left: auto;
    max-width: min(42%, 460px);
    max-height: 66%;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 0.75rem;
  }
  .battle-stage.layout-b .enemy-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
  }
  .battle-stage.layout-b .foe-plate {
    display: none; /* details live in TR target frame */
  }
  .battle-stage.layout-b .enemy-sprite-wrap {
    order: 1;
    aspect-ratio: auto;
    min-height: clamp(100px, 20vmin, 200px);
    padding-bottom: 0.9rem;
    position: relative;
    border: none;
    background: transparent;
    box-shadow: none;
  }
  .battle-stage.layout-b .enemy-strip.pack-solo .enemy-sprite-wrap {
    min-height: clamp(130px, 26vmin, 240px);
  }
  .battle-stage.layout-b .enemy-sprite {
    width: auto;
    height: clamp(88px, 20vmin, 200px);
    max-width: 92%;
    object-fit: contain;
    object-position: bottom center;
    image-rendering: auto;
  }
  .battle-stage.layout-b .enemy-strip.pack-solo .enemy-sprite {
    height: clamp(118px, 25vmin, 240px);
  }
  /* Hide per-sprite buff chips — shown in TR frame for focus */
  .battle-stage.layout-b .enemy-sprite-wrap > .buff-row.portrait-buffs {
    display: none;
  }

  /* Slim over-sprite HP only (names live in TL/TR frames) */
  .battle-stage.layout-b .sprite-hp {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translate(-50%, calc(-100% - 2px));
    z-index: 4;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    min-width: 44px;
    max-width: 88px;
    pointer-events: none;
  }
  .battle-stage.layout-b .sprite-hp-name {
    display: none; /* redundant with TR focus frame */
  }
  .battle-stage.layout-b .sprite-hp-track {
    width: 56px;
    max-width: 100%;
    height: 4px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid rgba(212, 164, 74, 0.4);
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
  }
  .battle-stage.layout-b .sprite-hp-fill {
    height: 100%;
    border-radius: inherit;
  }
  .battle-stage.layout-b .player-sprite-hp .sprite-hp-track {
    width: 60px;
  }

  /* Player field LEFT — sprite on marker (not giant card). Solo stays a single marker. */
  .battle-stage.layout-b .player-team {
    left: 6%;
    right: auto;
    bottom: 12%;
    width: min(30%, 320px);
    grid-template-columns: 1fr;
    grid-template-areas:
      "allies"
      "self";
    align-items: end;
    justify-items: center;
  }
  .battle-stage.layout-b .player-team.solo {
    grid-template-columns: 1fr;
    grid-template-areas: "self";
    width: min(24%, 250px);
  }
  /* Allies: readable left column (portrait + name + slim HP). Solo rules above are unchanged. */
  .battle-stage.layout-b .player-team:not(.solo) {
    left: 2.2%;
    width: min(46%, 520px);
    grid-template-columns: minmax(148px, 210px) minmax(0, 1fr);
    grid-template-areas: "allies self";
    justify-items: start;
    align-items: end;
    gap: 0.45rem;
  }
  .battle-stage.layout-b .ally-strip {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    justify-content: flex-end;
    width: 100%;
    max-height: min(46vh, 420px);
    overflow-x: hidden;
    overflow-y: auto;
    gap: 0.35rem;
    padding: 0;
  }
  .battle-stage.layout-b .ally-card {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    max-width: none;
    width: 100%;
    flex: 0 0 auto;
    gap: 0.35rem;
    padding: 0.18rem 0.4rem 0.22rem 0.12rem;
    background: rgba(8, 8, 10, 0.78);
    border: 1px solid rgba(212, 164, 74, 0.42);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
  .battle-stage.layout-b .ally-card .ally-meta {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    min-width: 0;
    flex: 1;
    padding-bottom: 0.2rem;
  }
  .battle-stage.layout-b .ally-card .ally-sub,
  .battle-stage.layout-b .ally-card .ally-numbers,
  .battle-stage.layout-b .ally-card .ally-bar.mana {
    display: none;
  }
  .battle-stage.layout-b .ally-name {
    display: block;
    font-size: 0.74rem;
    line-height: 1.15;
  }
  .battle-stage.layout-b .ally-bar {
    display: block;
    height: 4px;
    margin-top: 0.28rem;
  }
  .battle-stage.layout-b .ally-portrait {
    flex: 0 0 auto;
    width: auto;
    height: auto;
    overflow: visible;
  }
  .battle-stage.layout-b .ally-portrait img {
    width: auto;
    height: clamp(72px, 14vmin, 108px);
    max-width: 68px;
    object-fit: contain;
    object-position: bottom center;
    image-rendering: auto;
    filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.5));
  }
  .battle-stage.layout-b .player-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 0;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    min-width: 0;
    width: 100%;
  }
  .battle-stage.layout-b .player-panel .player-meta {
    display: none; /* details live in TL player frame */
  }
  .battle-stage.layout-b .player-bust {
    overflow: visible;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    min-height: clamp(118px, 24vmin, 220px);
    padding: 0 0 0.85rem;
    width: auto;
    max-width: 100%;
    border: none;
    background: transparent;
    box-shadow: none;
    border-radius: 0;
  }
  .battle-stage.layout-b .player-bust > .buff-row.portrait-buffs {
    display: none; /* buffs in TL frame */
  }
  .battle-stage.layout-b .player-bust img,
  .battle-stage.layout-b .player-sprite {
    position: relative;
    z-index: 1;
    width: auto;
    height: clamp(100px, 22vmin, 200px);
    max-width: 92%;
    object-fit: contain;
    object-position: bottom center;
    image-rendering: auto;
    border-radius: 0;
    border: none;
    box-shadow: none;
  }
  .battle-stage.layout-b .player-marker {
    display: block;
    bottom: 0;
    width: 70%;
    height: 16%;
    opacity: 0.78;
    z-index: 0;
  }

  .battle-stage.layout-b .ground-marker,
  .battle-stage.layout-b .target-ring {
    bottom: 0;
    width: 70%;
    height: 16%;
    opacity: 1;
    border: 1.5px dashed rgba(250, 204, 21, 0.55);
    border-radius: 50%;
    background: radial-gradient(
      ellipse at center,
      rgba(250, 204, 21, 0.16) 0%,
      rgba(212, 164, 74, 0.07) 45%,
      transparent 72%
    );
    box-shadow:
      0 0 10px rgba(250, 204, 21, 0.14),
      inset 0 0 6px rgba(250, 204, 21, 0.08);
  }
  .battle-stage.layout-b .ground-marker.focus,
  .battle-stage.layout-b .target-ring.focus {
    border-color: rgba(253, 224, 71, 0.88);
    border-style: solid;
    border-width: 2px;
    background: radial-gradient(
      ellipse at center,
      rgba(250, 204, 21, 0.28) 0%,
      rgba(234, 179, 8, 0.12) 42%,
      transparent 74%
    );
    box-shadow:
      0 0 14px rgba(250, 204, 21, 0.32),
      0 0 4px rgba(253, 224, 71, 0.45),
      inset 0 0 8px rgba(250, 204, 21, 0.12);
    animation: layoutBFocusPulse 1.8s ease-in-out infinite;
  }
  .battle-stage.layout-b.reduced-motion .ground-marker.focus,
  .battle-stage.layout-b.reduced-motion .target-ring.focus {
    animation: none;
  }
  @keyframes layoutBFocusPulse {
    0%, 100% { filter: brightness(1); }
    50% { filter: brightness(1.12); }
  }
  .battle-stage.layout-b .ground-marker.dead {
    opacity: 0.2 !important;
    border-style: dotted;
    filter: grayscale(0.8);
    animation: none;
    box-shadow: none;
  }

  .battle-stage.layout-b .arena-art {
    filter: brightness(0.38) saturate(0.7) contrast(1.08);
  }
  .battle-stage.layout-b .arena-vignette {
    background:
      radial-gradient(ellipse at 55% 48%, transparent 18%, rgba(0, 0, 0, 0.5) 58%, rgba(0, 0, 0, 0.88) 100%),
      linear-gradient(to top, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.28) 30%, transparent 50%),
      linear-gradient(to bottom, rgba(0, 0, 0, 0.45) 0%, transparent 24%),
      linear-gradient(to right, rgba(0, 0, 0, 0.42) 0%, transparent 22%, transparent 78%, rgba(0, 0, 0, 0.42) 100%);
  }

  @media (max-width: 768px) {
    .battle-stage.layout-b .layout-b-frame {
      width: min(46%, 200px);
      top: 0.35rem;
      padding: 0.35rem 0.4rem;
    }
    .battle-stage.layout-b .layout-b-frame.player-frame { left: 0.3rem; }
    .battle-stage.layout-b .layout-b-frame.target-frame { right: 0.3rem; }
    .battle-stage.layout-b .lb-name { font-size: 0.72rem; }
    .battle-stage.layout-b .sprite-hp-name { display: none; }
    .battle-stage.layout-b .enemy-strip,
    .battle-stage.layout-b .enemy-strip.pack-solo,
    .battle-stage.layout-b .enemy-strip.pack-duo,
    .battle-stage.layout-b .enemy-strip.pack-swarm {
      /* Full-width centered row under the TL/TR frames. The old narrow column
         wrapped every extra foe onto its own row, so mid-fight adds stacked
         upward under the HUD and behind the action toast. */
      top: auto;
      bottom: auto;
      right: auto;
      left: auto;
      width: 100%;
      max-width: none;
      flex-wrap: wrap;
      align-content: flex-start;
      align-self: start;
      align-items: flex-end;
      justify-content: center;
      gap: 0.4rem;
      padding: 7.5rem 0.5rem 0;
    }
    .battle-stage.layout-b .enemy-strip .enemy-card {
      flex: 0 0 auto;
    }
    /* Phase / wind-up banners are full-width flex items: keep them on a row
       below the foes so they never squeeze the sprite row to zero width. */
    .battle-stage.layout-b .enemy-strip > .phase-banner,
    .battle-stage.layout-b .enemy-strip > .telegraph-banner {
      order: 1;
    }
    /* Toast sits between the enemy row and the player, never over a sprite. */
    .battle-stage.layout-b .action-banner-stack {
      top: 50%;
    }
    .battle-stage.layout-b .player-team,
    .battle-stage.layout-b .player-team.solo {
      left: 4%;
      bottom: 11%;
      width: min(38%, 170px);
    }
    .battle-stage.layout-b .player-team:not(.solo) {
      left: 2%;
      width: min(58%, 240px);
      grid-template-columns: 1fr;
      grid-template-areas:
        "allies"
        "self";
    }
    .battle-stage.layout-b .ally-strip {
      max-height: 30vh;
    }
    .battle-stage.layout-b .ally-portrait img {
      height: clamp(52px, 14vmin, 76px);
    }
    .battle-stage.layout-b .enemy-sprite {
      height: clamp(72px, 18vmin, 140px);
    }
    .battle-stage.layout-b .player-bust img,
    .battle-stage.layout-b .player-sprite {
      height: clamp(84px, 20vmin, 160px);
    }
  }


</style>
